import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Stack,
  IconButton,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Autocomplete,
  Tabs,
  Tab,
  LinearProgress,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import DownloadIcon from '@mui/icons-material/Download'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import EditIcon from '@mui/icons-material/Edit'
import HistoryIcon from '@mui/icons-material/History'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CloseIcon from '@mui/icons-material/Close'
import CancelIcon from '@mui/icons-material/Cancel'
import LinkIcon from '@mui/icons-material/Link'
import AddBusinessIcon from '@mui/icons-material/AddBusiness'
import SearchIcon from '@mui/icons-material/Search'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import UndoIcon from '@mui/icons-material/Undo'
import { inventorySyncAdminService, productService } from '../services'
import { getApiErrorMessage } from '../services/apiClient'
import {
  validatePositiveSellingPriceAndMrp,
} from '../utils/sellingPriceMrp'

const STATUS_LABELS = {
  submitted: 'Submitted',
  approved: 'Approved',
  rejected: 'Rejected',
  reverted: 'Reverted',
}

const REVERT_POLL_INTERVAL_MS = 2000
const REVERT_POLL_MAX_MS = 10 * 60 * 1000

const APPROVE_ALL_BATCH_SIZE = 100

const chunkArray = (items, size) => {
  const chunks = []
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size))
  }
  return chunks
}

const getStatusChipColor = (status) => {
  switch (status) {
    case 'submitted':
      return 'warning'
    case 'approved':
      return 'success'
    case 'rejected':
      return 'error'
    case 'reverted':
      return 'default'
    default:
      return 'default'
  }
}

const formatCount = (value) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return '0'
  return n.toLocaleString('en-US')
}

const getNewProductReviewChipColor = (status) => {
  switch (status) {
    case 'approved':
      return 'success'
    case 'rejected':
      return 'error'
    default:
      return 'warning'
  }
}

const getLineReviewChipColor = (status) => getNewProductReviewChipColor(status)

const formatLineReviewLabel = (status) => {
  if (!status) return 'Pending'
  return status.charAt(0).toUpperCase() + status.slice(1)
}

const isLineItemSelectable = (item) =>
  !item.line_review_status || item.line_review_status === 'pending'

/** Normalize line-item ids so Set membership is stable across number/string API values. */
const toLineItemSelectionId = (id) => {
  const n = Number(id)
  return Number.isFinite(n) && n > 0 ? n : null
}

const isNewProductPending = (item) =>
  item?.line_type === 'new' &&
  (!item.new_product_review_status || item.new_product_review_status === 'pending')

/**
 * Product-origin tabs (Internal vs External) follow catalog/new-product source,
 * not the is_non_shwapno inventory flag (retailer-owned stock vs Shwapno warehouse).
 */
const isExternalLineItem = (item) => {
  if (
    item?.resolution_kind === 'existing_external' ||
    item?.resolution_kind === 'new_external'
  ) {
    return true
  }
  if (
    item?.resolution_kind === 'existing_shwapno' ||
    item?.resolution_kind === 'new_shwapno'
  ) {
    return false
  }
  return (
    item?.new_product_source === 'external' ||
    item?.catalog_source === 'external'
  )
}

const isInternalLineItem = (item) => !isExternalLineItem(item)

const getLineOriginLabel = (item) => {
  if (item?.resolution_kind === 'existing_external') return 'Existing external'
  if (item?.resolution_kind === 'existing_shwapno') return 'Shwapno catalog'
  if (item?.resolution_kind === 'new_external') return 'New external'
  if (item?.resolution_kind === 'new_shwapno') return 'New Shwapno'
  if (item?.catalog_source === 'external') return 'Existing external'
  if (item?.new_product_source === 'external') return 'New external'
  if (item?.line_type === 'existing') return 'Shwapno catalog'
  if (item?.new_product_source === 'shwapno') return 'New Shwapno'
  return null
}

const getLineReviewStatus = (item) => item?.line_review_status || 'pending'

/** Pending lines that approve-all will attempt (excludes already approved/rejected). */
const isPendingLineItemForApproval = (item) =>
  getLineReviewStatus(item) === 'pending'

const normalizeLineItemSearchText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()

const getLineItemSearchHaystack = (item) => {
  const parts = [item?.product_name, item?.article_code, item?.sku]
  if (Array.isArray(item?.barcodes)) {
    parts.push(...item.barcodes.map((b) => String(b).trim()).filter(Boolean))
  }
  return parts.filter(Boolean).join(' ').toLowerCase()
}

/** Match product name or article code (supports multi-word queries). */
const matchesLineItemSearch = (item, query) => {
  const normalized = normalizeLineItemSearchText(query)
  if (!normalized) return true
  const haystack = getLineItemSearchHaystack(item)
  const tokens = normalized.split(/\s+/).filter(Boolean)
  return tokens.every((token) => haystack.includes(token))
}

const LINE_ITEM_SECTIONS = {
  all: {
    key: 'all',
    label: 'All',
    description: 'All line items in this inventory sync.',
    matches: () => true,
  },
  internal: {
    key: 'internal',
    label: 'Internal Products',
    description:
      "Products that already exist in Shwapno's internal product catalog and have been synced.",
    matches: isInternalLineItem,
  },
  external: {
    key: 'external',
    label: 'External Products',
    description: 'Products received from external sources.',
    matches: isExternalLineItem,
  },
}

/** Line-item review status filter (client-side; detail API already returns all rows). */
const LINE_REVIEW_STATUS_FILTERS = {
  all: {
    key: 'all',
    label: 'All statuses',
    matches: () => true,
  },
  pending: {
    key: 'pending',
    label: 'Pending',
    matches: (item) => getLineReviewStatus(item) === 'pending',
  },
  approved: {
    key: 'approved',
    label: 'Approved',
    matches: (item) => getLineReviewStatus(item) === 'approved',
  },
  rejected: {
    key: 'rejected',
    label: 'Rejected',
    matches: (item) => getLineReviewStatus(item) === 'rejected',
  },
}

const catalogProductLabel = (product) => {
  if (!product) return ''
  const name = product.product_name || product.name || 'Unnamed product'
  const sku = product.sku || product.article_code || ''
  return sku ? `${name} (${sku})` : name
}

const isCatalogProductNonShwapno = (product) =>
  product?.source === 'external' ||
  product?.is_non_shwapno === true ||
  product?.isNonShwapno === true

const formatCatalogMrp = (product) => {
  const raw =
    product?.unit_price ?? product?.mrp ?? product?.current_unit_price ?? null
  if (raw == null || raw === '') return null
  const n = Number(raw)
  if (Number.isNaN(n)) return null
  return `৳${n.toFixed(2)}`
}

const formatDate = (value) => {
  if (!value) return '-'
  try {
    return new Date(value).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  } catch {
    return String(value)
  }
}

const formatDateOnly = (value) => {
  if (!value) return '-'
  try {
    return new Date(value).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return String(value)
  }
}

const formatCurrency = (value) => {
  const n = Number(value)
  if (Number.isNaN(n)) return '-'
  return n.toFixed(2)
}

const toEditableDate = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toISOString().slice(0, 10)
}

const csvEscape = (value) => {
  const text = value == null ? '' : String(value)
  if (/[",\n\r]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`
  }
  return text
}

const approveButtonSx = {
  bgcolor: '#4CAF50',
  color: '#FFFFFF',
  '&:hover': {
    bgcolor: '#45a049',
  },
}

/** Selection toolbar actions: wrap cleanly from phone → desktop. */
const selectionActionButtonSx = {
  whiteSpace: 'nowrap',
  textTransform: 'none',
  flex: { xs: '1 1 calc(50% - 4px)', sm: '1 1 auto', md: '0 1 auto' },
  minWidth: { xs: 0, md: 'auto' },
}

const SelectionActionLabel = ({ shortLabel, fullLabel }) => (
  <>
    <Box component="span" sx={{ display: { xs: 'inline', lg: 'none' } }}>
      {shortLabel}
    </Box>
    <Box component="span" sx={{ display: { xs: 'none', lg: 'inline' } }}>
      {fullLabel}
    </Box>
  </>
)

const lineItemsTableHeaderCellSx = {
  fontWeight: 600,
  color: '#1a1a1a',
  py: 1.75,
  borderBottom: '2px solid #e8eaed',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
}

const lineItemsTableBodyCellSx = {
  py: 2.25,
  verticalAlign: 'middle',
  borderBottom: '1px solid #eee',
}

const lineItemsTableNumericCellSx = {
  ...lineItemsTableBodyCellSx,
  fontVariantNumeric: 'tabular-nums',
  fontWeight: 500,
}

const lineItemActionButtonSx = {
  minHeight: 32,
  textTransform: 'none',
  whiteSpace: 'nowrap',
}

const barcodesFromItem = (item) => {
  if (!Array.isArray(item?.barcodes)) return []
  return item.barcodes.map((b) => String(b).trim()).filter(Boolean)
}

const LineItemBarcodes = ({ item }) => {
  const codes = barcodesFromItem(item)
  if (codes.length === 0) {
    return (
      <Typography variant="caption" display="block" sx={{ color: '#888', mt: 0.5 }}>
        No barcodes
      </Typography>
    )
  }
  return (
    <Box sx={{ mt: 0.75 }}>
      <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
        Barcodes
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {codes.map((code) => (
          <Chip
            key={code}
            size="small"
            label={code}
            variant="outlined"
            sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
          />
        ))}
      </Box>
    </Box>
  )
}

const parseInventorySyncApiError = (err) => {
  const data = err?.response?.data
  const message = getApiErrorMessage(err, 'Request failed')
  if (!data || typeof data === 'string') {
    return {
      message,
      code: null,
      newProductId: null,
      sku: null,
      existingProduct: null,
    }
  }
  return {
    message,
    code: data.code || null,
    newProductId: data.new_product_id || null,
    sku: data.sku || null,
    existingProduct: data.existing_product || null,
  }
}

const isSkuAlreadyExistsError = (parsed) =>
  parsed?.code === 'INVENTORY_SYNC_SKU_ALREADY_EXISTS' &&
  Boolean(parsed?.existingProduct?.id)

const isExistingProductLinkable = (product) =>
  !!product?.id &&
  product.is_active !== false &&
  (product.status == null || product.status === 'published')

const isFixableNewProductApproveError = (parsed) => {
  const msg = parsed?.message || ''
  return (
    parsed?.code === 'INVENTORY_SYNC_ARTICLE_CODE_REQUIRED' ||
    /article code/i.test(msg) ||
    /barcode/i.test(msg)
  )
}

const InventorySyncDetailPage = () => {
  const navigate = useNavigate()
  const { syncId } = useParams()

  const [sync, setSync] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [approveDialogOpen, setApproveDialogOpen] = useState(false)
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteReason, setDeleteReason] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [revertDialogOpen, setRevertDialogOpen] = useState(false)
  const [revertReason, setRevertReason] = useState('')
  const [revertPreflight, setRevertPreflight] = useState(null)
  const [revertPreflightLoading, setRevertPreflightLoading] = useState(false)
  const [reverting, setReverting] = useState(false)
  const [revertProgress, setRevertProgress] = useState(null)
  const revertPollTimerRef = useRef(null)
  const [auditLog, setAuditLog] = useState(null)
  const [auditLoading, setAuditLoading] = useState(false)
  const [activeTab, setActiveTab] = useState('line-items')
  const [rejectReason, setRejectReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [actionMessage, setActionMessage] = useState(null)
  const [itemPage, setItemPage] = useState(0)
  const [itemRowsPerPage, setItemRowsPerPage] = useState(10)
  const [lineItemSection, setLineItemSection] = useState('all')
  const [lineReviewStatusFilter, setLineReviewStatusFilter] = useState('all')
  const [lineItemSearchQuery, setLineItemSearchQuery] = useState('')
  const [modifyDialogOpen, setModifyDialogOpen] = useState(false)
  const [uploadingCsv, setUploadingCsv] = useState(false)
  const [selectedCsvFile, setSelectedCsvFile] = useState(null)
  const [csvResult, setCsvResult] = useState(null)
  const [editingItem, setEditingItem] = useState(null)
  const [rowEditOpen, setRowEditOpen] = useState(false)
  const [rowEditSaving, setRowEditSaving] = useState(false)
  const [rowForm, setRowForm] = useState({
    quantity: '',
    unit_cost: '',
    unit_price: '',
    expiry_date: '',
    is_non_shwapno: false,
    product_name: '',
    article_code: '',
    barcodes: [],
    image_url: '',
  })
  const [barcodeInput, setBarcodeInput] = useState('')
  const [approveNewProductId, setApproveNewProductId] = useState(null)
  const [rejectNewProductId, setRejectNewProductId] = useState(null)
  const [rejectNewProductReason, setRejectNewProductReason] = useState('')
  const [approveNewProductError, setApproveNewProductError] = useState(null)
  const [skuConflictPrompt, setSkuConflictPrompt] = useState(null)
  const [skuConflictSubmitting, setSkuConflictSubmitting] = useState(false)
  const [rowEditError, setRowEditError] = useState(null)
  const [linkDialogError, setLinkDialogError] = useState(null)
  const [selectedLineItemIds, setSelectedLineItemIds] = useState(() => new Set())
  const [lineItemBulkErrors, setLineItemBulkErrors] = useState({})
  const [bulkRejectDialogOpen, setBulkRejectDialogOpen] = useState(false)
  const [approveAllDialogOpen, setApproveAllDialogOpen] = useState(false)
  const [approveAllProgress, setApproveAllProgress] = useState(null)
  const [bulkRejectReason, setBulkRejectReason] = useState('')
  const [linkDialogOpen, setLinkDialogOpen] = useState(false)
  const [linkProductSearch, setLinkProductSearch] = useState('')
  const [linkProductOptions, setLinkProductOptions] = useState([])
  const [linkProductLoading, setLinkProductLoading] = useState(false)
  const [selectedLinkProduct, setSelectedLinkProduct] = useState(null)
  const [linkSubmitting, setLinkSubmitting] = useState(false)
  const [createInternalSubmitting, setCreateInternalSubmitting] = useState(false)
  const [bulkLinkDialogOpen, setBulkLinkDialogOpen] = useState(false)
  const [bulkLinkUploading, setBulkLinkUploading] = useState(false)
  const [bulkLinkConfirming, setBulkLinkConfirming] = useState(false)
  const [selectedBulkLinkCsvFile, setSelectedBulkLinkCsvFile] = useState(null)
  const [bulkLinkPreview, setBulkLinkPreview] = useState(null)
  const [bulkLinkConfirmResult, setBulkLinkConfirmResult] = useState(null)
  const csvInputRef = useRef(null)
  const bulkLinkCsvInputRef = useRef(null)

  const fetchSync = useCallback(
    async ({ resetView = false, showLoading = false } = {}) => {
      if (!syncId) return
      try {
        if (showLoading) setLoading(true)
        setError(null)
        const response = await inventorySyncAdminService.getDetail(syncId)
        setSync(response)
        if (resetView) {
          setItemPage(0)
          setLineItemSection('all')
          setLineReviewStatusFilter('all')
          setLineItemSearchQuery('')
          setSelectedLineItemIds(new Set())
        }
      } catch (err) {
        console.error('Failed to load sync detail', err)
        setError(err.response?.data?.message || 'Failed to load sync')
      } finally {
        if (showLoading) setLoading(false)
      }
    },
    [syncId],
  )

  useEffect(() => {
    fetchSync({ resetView: true, showLoading: true })
  }, [fetchSync])

  useEffect(() => {
    if (!linkDialogOpen) return undefined
    const timer = setTimeout(async () => {
      try {
        setLinkProductLoading(true)
        const response = await inventorySyncAdminService.getProducts({
          search: linkProductSearch.trim() || undefined,
          page: 1,
          limit: 25,
        })
        setLinkProductOptions(response.items || [])
      } catch (err) {
        console.error('Failed to search catalog products', err)
        setLinkProductOptions([])
      } finally {
        setLinkProductLoading(false)
      }
    }, 300)
    return () => clearTimeout(timer)
  }, [linkDialogOpen, linkProductSearch])

  const allItems = useMemo(() => sync?.items || [], [sync])

  /** Pending line items eligible for selection / bulk approve-reject. */
  const eligibleSelectableLineItemIds = useMemo(() => {
    const ids = new Set()
    for (const item of allItems) {
      if (!isLineItemSelectable(item)) continue
      const id = toLineItemSelectionId(item.id)
      if (id != null) ids.add(id)
    }
    return ids
  }, [allItems])

  // Keep selection ⊆ eligible ids so approve/reject (line, new-product, bulk, refetch)
  // never leaves terminal rows checked+disabled in the selection toolbar.
  useEffect(() => {
    setSelectedLineItemIds((prev) => {
      if (prev.size === 0) return prev
      const next = new Set()
      let changed = false
      for (const rawId of prev) {
        const id = toLineItemSelectionId(rawId)
        if (id != null && eligibleSelectableLineItemIds.has(id)) {
          next.add(id)
          if (id !== rawId) changed = true
        } else {
          changed = true
        }
      }
      if (!changed && next.size === prev.size) return prev
      return next
    })
  }, [eligibleSelectableLineItemIds])

  const lineItemSectionCounts = useMemo(
    () => ({
      all: allItems.length,
      internal: allItems.filter(isInternalLineItem).length,
      external: allItems.filter(isExternalLineItem).length,
    }),
    [allItems],
  )

  const sectionFilteredItems = useMemo(() => {
    const matcher =
      LINE_ITEM_SECTIONS[lineItemSection]?.matches || LINE_ITEM_SECTIONS.all.matches
    return allItems.filter(matcher)
  }, [allItems, lineItemSection])

  const searchFilteredItems = useMemo(() => {
    const query = lineItemSearchQuery.trim()
    if (!query) return sectionFilteredItems
    return sectionFilteredItems.filter((item) => matchesLineItemSearch(item, query))
  }, [sectionFilteredItems, lineItemSearchQuery])

  const lineReviewStatusCounts = useMemo(
    () => ({
      all: searchFilteredItems.length,
      pending: searchFilteredItems.filter(
        (item) => getLineReviewStatus(item) === 'pending',
      ).length,
      approved: searchFilteredItems.filter(
        (item) => getLineReviewStatus(item) === 'approved',
      ).length,
      rejected: searchFilteredItems.filter(
        (item) => getLineReviewStatus(item) === 'rejected',
      ).length,
    }),
    [searchFilteredItems],
  )

  const filteredItems = useMemo(() => {
    const statusMatcher =
      LINE_REVIEW_STATUS_FILTERS[lineReviewStatusFilter]?.matches ||
      LINE_REVIEW_STATUS_FILTERS.all.matches
    return searchFilteredItems.filter(statusMatcher)
  }, [searchFilteredItems, lineReviewStatusFilter])

  const pagedItems = useMemo(
    () =>
      filteredItems.slice(
        itemPage * itemRowsPerPage,
        itemPage * itemRowsPerPage + itemRowsPerPage,
      ),
    [filteredItems, itemPage, itemRowsPerPage],
  )

  // Keep current page in range when the filtered list shrinks (e.g. section change).
  useEffect(() => {
    const maxPage = Math.max(
      0,
      Math.ceil(filteredItems.length / itemRowsPerPage) - 1,
    )
    if (itemPage > maxPage) setItemPage(maxPage)
  }, [filteredItems.length, itemRowsPerPage, itemPage])

  const handleItemPageChange = (_, newPage) => setItemPage(newPage)

  const handleItemRowsPerPageChange = (e) => {
    setItemRowsPerPage(parseInt(e.target.value, 10))
    setItemPage(0)
  }

  const lineItemsPagination =
    filteredItems.length > 0 ? (
      <TablePagination
        component="div"
        count={filteredItems.length}
        page={itemPage}
        onPageChange={handleItemPageChange}
        rowsPerPage={itemRowsPerPage}
        onRowsPerPageChange={handleItemRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    ) : null

  const selectablePagedItems = useMemo(
    () => pagedItems.filter(isLineItemSelectable),
    [pagedItems],
  )

  const selectedEligibleLineItemIds = useMemo(
    () => [...selectedLineItemIds].filter((id) => eligibleSelectableLineItemIds.has(id)),
    [selectedLineItemIds, eligibleSelectableLineItemIds],
  )

  // Count/check only eligible ids so UI stays correct on the same render as a status
  // change; the prune effect then removes stale ids from selectedLineItemIds.
  const selectedCount = selectedEligibleLineItemIds.length

  const isLineItemIdSelected = (lineItemId) => {
    const id = toLineItemSelectionId(lineItemId)
    return id != null && selectedLineItemIds.has(id) && eligibleSelectableLineItemIds.has(id)
  }

  const allVisibleSelectableSelected =
    selectablePagedItems.length > 0 &&
    selectablePagedItems.every((item) => isLineItemIdSelected(item.id))

  const someVisibleSelectableSelected =
    selectablePagedItems.some((item) => isLineItemIdSelected(item.id)) &&
    !allVisibleSelectableSelected

  const toggleLineItemSelected = (lineItemId) => {
    const id = toLineItemSelectionId(lineItemId)
    if (id == null || !eligibleSelectableLineItemIds.has(id)) return
    setSelectedLineItemIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleSelectAllVisible = () => {
    setSelectedLineItemIds((prev) => {
      const next = new Set(prev)
      if (allVisibleSelectableSelected) {
        selectablePagedItems.forEach((item) => {
          const id = toLineItemSelectionId(item.id)
          if (id != null) next.delete(id)
        })
      } else {
        selectablePagedItems.forEach((item) => {
          const id = toLineItemSelectionId(item.id)
          if (id != null && eligibleSelectableLineItemIds.has(id)) next.add(id)
        })
      }
      return next
    })
  }

  const clearLineItemSelection = () => {
    setSelectedLineItemIds(new Set())
    setLineItemBulkErrors({})
  }

  const removeLineItemsFromSelection = (lineItemIds) => {
    const toRemove = new Set()
    for (const rawId of lineItemIds) {
      const id = toLineItemSelectionId(rawId)
      if (id != null) toRemove.add(id)
    }
    if (toRemove.size === 0) return
    setSelectedLineItemIds((prev) => {
      if (prev.size === 0) return prev
      const next = new Set(prev)
      let changed = false
      toRemove.forEach((id) => {
        if (next.delete(id)) changed = true
      })
      return changed ? next : prev
    })
  }

  /** One approve/reject control per new-product request (first line item in list order). */
  const primaryLineIdByNewProduct = useMemo(() => {
    const map = new Map()
    for (const item of allItems) {
      if (item.line_type !== 'new' || !item.inventory_sync_new_product_id) continue
      const npId = item.inventory_sync_new_product_id
      if (!map.has(npId)) map.set(npId, item.id)
    }
    return map
  }, [allItems])

  const bulkLinkEligibleItems = useMemo(
    () =>
      allItems.filter(
        (item) =>
          isNewProductPending(item) &&
          item.inventory_sync_new_product_id &&
          primaryLineIdByNewProduct.get(item.inventory_sync_new_product_id) === item.id,
      ),
    [allItems, primaryLineIdByNewProduct],
  )

  const handleApprove = async () => {
    const approvedLineCount = (sync?.items || []).filter(
      (row) => row.line_review_status === 'approved',
    ).length
    if (approvedLineCount === 0) {
      setApproveDialogOpen(false)
      setError(
        'Cannot approve sync: all line items are rejected. Reject the sync instead, or approve at least one line item.',
      )
      return
    }
    try {
      setSubmitting(true)
      setError(null)
      await inventorySyncAdminService.approve(syncId)
      setApproveDialogOpen(false)
      setActionMessage({ severity: 'success', text: 'Inventory sync approved.' })
      await fetchSync({ resetView: false, showLoading: false })
    } catch (err) {
      console.error('Approve failed', err)
      setError(err.response?.data?.message || 'Failed to approve sync')
    } finally {
      setSubmitting(false)
    }
  }

  const handleReject = async () => {
    try {
      setSubmitting(true)
      setError(null)
      await inventorySyncAdminService.reject(syncId, rejectReason || undefined)
      setRejectDialogOpen(false)
      setRejectReason('')
      setActionMessage({ severity: 'info', text: 'Inventory sync rejected.' })
      await fetchSync({ resetView: false, showLoading: false })
    } catch (err) {
      console.error('Reject failed', err)
      setError(err.response?.data?.message || 'Failed to reject sync')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async () => {
    try {
      setDeleting(true)
      setError(null)
      await inventorySyncAdminService.delete(syncId, deleteReason || undefined)
      setDeleteDialogOpen(false)
      setDeleteReason('')
      navigate('/inventory-sync')
    } catch (err) {
      console.error('Delete failed', err)
      setError(err.response?.data?.message || 'Failed to delete sync')
      setDeleteDialogOpen(false)
    } finally {
      setDeleting(false)
    }
  }

  const clearRevertPoll = useCallback(() => {
    if (revertPollTimerRef.current) {
      clearInterval(revertPollTimerRef.current)
      revertPollTimerRef.current = null
    }
  }, [])

  useEffect(() => () => clearRevertPoll(), [clearRevertPoll])

  const openRevertDialog = async () => {
    setRevertDialogOpen(true)
    setRevertReason('')
    setRevertPreflight(null)
    setRevertPreflightLoading(true)
    setError(null)
    try {
      const preflight = await inventorySyncAdminService.getRevertPreflight(syncId)
      setRevertPreflight(preflight)
    } catch (err) {
      console.error('Revert preflight failed', err)
      setError(err.response?.data?.message || 'Failed to load revert preview')
      setRevertDialogOpen(false)
    } finally {
      setRevertPreflightLoading(false)
    }
  }

  const pollUntilReverted = useCallback(async () => {
    const startedAt = Date.now()
    clearRevertPoll()
    return new Promise((resolve) => {
      const tick = async () => {
        try {
          const detail = await inventorySyncAdminService.getDetail(syncId)
          setSync(detail)
          const revertStatus = detail?.meta?.revert_status
          if (detail?.status === 'reverted') {
            clearRevertPoll()
            resolve({ ok: true, detail })
            return
          }
          if (revertStatus === 'failed') {
            clearRevertPoll()
            resolve({ ok: false, detail, failed: true })
            return
          }
          if (Date.now() - startedAt >= REVERT_POLL_MAX_MS) {
            clearRevertPoll()
            resolve({ ok: false, detail, timedOut: true })
            return
          }
          setRevertProgress((prev) => ({
            ...(prev || {}),
            progress: detail?.meta?.revert_progress || prev?.progress || null,
          }))
        } catch (err) {
          console.error('Revert poll failed', err)
        }
      }
      tick()
      revertPollTimerRef.current = setInterval(tick, REVERT_POLL_INTERVAL_MS)
    })
  }, [clearRevertPoll, syncId])

  const handleRevert = async () => {
    try {
      setReverting(true)
      setError(null)
      const queued = await inventorySyncAdminService.revert(
        syncId,
        revertReason || undefined,
      )
      setRevertDialogOpen(false)
      setRevertReason('')
      if (queued?.status === 'reverted') {
        setActionMessage({
          severity: 'success',
          text: 'Inventory sync was already reverted.',
        })
        setAuditLog(null)
        await fetchSync({ resetView: false, showLoading: false })
        return
      }
      setRevertProgress({
        message: 'Removing stock added by this sync…',
        progress: queued?.preflight || null,
      })
      const result = await pollUntilReverted()
      setRevertProgress(null)
      setAuditLog(null)
      if (result.ok) {
        setActionMessage({
          severity: 'success',
          text: 'Inventory sync reverted. Sync stock has been removed from the shop.',
        })
        if (activeTab === 'audit') {
          // Force refetch after clearing cached log
          setAuditLoading(true)
          try {
            const entries = await inventorySyncAdminService.getAuditLog(syncId)
            setAuditLog(entries)
          } catch (err) {
            console.error('Failed to reload audit log', err)
            setAuditLog([])
          } finally {
            setAuditLoading(false)
          }
        }
      } else if (result.failed) {
        setError(
          result.detail?.meta?.revert_error ||
            'Revert failed. Please try again or contact support.',
        )
      } else if (result.timedOut) {
        setActionMessage({
          severity: 'info',
          text: 'Revert is still running. Refresh this page in a few minutes to confirm status.',
        })
      }
    } catch (err) {
      console.error('Revert failed', err)
      setError(err.response?.data?.message || 'Failed to revert sync')
      setRevertProgress(null)
    } finally {
      setReverting(false)
    }
  }

  const fetchAuditLog = useCallback(async () => {
    if (auditLog !== null) return // already loaded
    setAuditLoading(true)
    try {
      const entries = await inventorySyncAdminService.getAuditLog(syncId)
      setAuditLog(entries)
    } catch (err) {
      console.error('Failed to load audit log', err)
      setAuditLog([])
    } finally {
      setAuditLoading(false)
    }
  }, [syncId, auditLog])

  const handleExportCsv = async () => {
    try {
      setExporting(true)
      setError(null)
      const res = await inventorySyncAdminService.exportCsv(syncId)
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `inventory-sync-${syncId}.csv`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Export failed', err)
      setError(err.response?.data?.message || 'Failed to export CSV')
    } finally {
      setExporting(false)
    }
  }

  const withReviewCounts = useCallback((nextSync) => {
    const items = nextSync.items || []
    const newProducts = nextSync.new_products || []
    const pendingLineCount = items.filter(
      (row) => !row.line_review_status || row.line_review_status === 'pending',
    ).length
    const approvedLineCount = items.filter(
      (row) => row.line_review_status === 'approved',
    ).length

    const pendingNewProductIds = new Set()
    for (const np of newProducts) {
      if (!np.review_status || np.review_status === 'pending') {
        pendingNewProductIds.add(np.id)
      }
    }
    for (const item of items) {
      const npId = item.inventory_sync_new_product_id
      if (!npId) continue
      if (
        item.line_type === 'new' &&
        (!item.new_product_review_status || item.new_product_review_status === 'pending')
      ) {
        pendingNewProductIds.add(npId)
      } else if (item.new_product_review_status === 'approved') {
        pendingNewProductIds.delete(npId)
      }
    }
    const pendingNew = pendingNewProductIds.size

    return {
      ...nextSync,
      pending_line_items_count: pendingLineCount,
      pending_new_products_count: pendingNew,
      // Require at least one approved line; all-rejected syncs must be rejected.
      can_approve_sync:
        pendingNew === 0 && pendingLineCount === 0 && approvedLineCount > 0,
    }
  }, [])

  const updateItemInState = useCallback(
    (updatedItem) => {
      setSync((prev) => {
        if (!prev) return prev
        const newProductId = updatedItem.inventory_sync_new_product_id
        const identityPatch =
          newProductId && updatedItem.line_type === 'new'
            ? {
                product_name: updatedItem.product_name,
                article_code: updatedItem.article_code,
                barcodes: updatedItem.barcodes,
                image_url: updatedItem.image_url,
              }
            : null
        return withReviewCounts({
          ...prev,
          items: (prev.items || []).map((item) => {
            if (Number(item.id) === Number(updatedItem.id)) {
              return { ...item, ...updatedItem }
            }
            if (
              identityPatch &&
              item.inventory_sync_new_product_id === newProductId
            ) {
              return { ...item, ...identityPatch }
            }
            return item
          }),
          new_products: (prev.new_products || []).map((np) =>
            np.id === newProductId ? { ...np, ...identityPatch } : np,
          ),
        })
      })
    },
    [withReviewCounts],
  )

  const applyBulkLineItemUpdates = useCallback(
    (updatedItems) => {
      if (!Array.isArray(updatedItems) || updatedItems.length === 0) return
      setSync((prev) => {
        if (!prev) return prev
        const byId = new Map(updatedItems.map((item) => [Number(item.id), item]))
        return withReviewCounts({
          ...prev,
          items: (prev.items || []).map((item) => {
            const patch = byId.get(Number(item.id))
            return patch ? { ...item, ...patch } : item
          }),
        })
      })
    },
    [withReviewCounts],
  )

  /** Apply bulk approve API result in one state update so review counts stay in sync. */
  const applyBulkApproveResult = useCallback(
    (result) => {
      const updatedItems = result?.updated || []
      const newProductSummaries = result?.new_products_updated || []
      if (updatedItems.length === 0 && newProductSummaries.length === 0) return

      setSync((prev) => {
        if (!prev) return prev
        const npById = new Map(newProductSummaries.map((np) => [np.id, np]))
        const lineById = new Map(updatedItems.map((item) => [Number(item.id), item]))

        const items = (prev.items || []).map((item) => {
          const npId = item.inventory_sync_new_product_id
          const npSummary = npId ? npById.get(npId) : null
          const linePatch = lineById.get(Number(item.id))
          let next = item
          if (npSummary) {
            next = {
              ...next,
              product_name: npSummary.product_name ?? next.product_name,
              article_code: npSummary.article_code ?? next.article_code,
              barcodes: npSummary.barcodes ?? next.barcodes,
              image_url: npSummary.image_url ?? next.image_url,
              new_product_source: npSummary.source ?? next.new_product_source,
              new_product_review_status: npSummary.review_status,
              new_product_rejection_reason: npSummary.rejection_reason ?? null,
              admin_product_id: npSummary.admin_product_id ?? next.admin_product_id,
            }
          }
          if (linePatch) {
            next = { ...next, ...linePatch }
          }
          return next
        })

        const new_products = (prev.new_products || []).map((np) =>
          npById.has(np.id) ? { ...np, ...npById.get(np.id) } : np,
        )

        return withReviewCounts({
          ...prev,
          items,
          new_products,
        })
      })
    },
    [withReviewCounts],
  )

  /** Patch all line items + new_products row after approve/reject/link of a new product. */
  const applyNewProductReviewInState = useCallback(
    (npSummary) => {
      if (!npSummary?.id) return
      setSync((prev) => {
        if (!prev) return prev
        const npId = npSummary.id
        const rejected = npSummary.review_status === 'rejected'
        return withReviewCounts({
          ...prev,
          items: (prev.items || []).map((item) => {
            if (item.inventory_sync_new_product_id !== npId) return item
            return {
              ...item,
              product_name: npSummary.product_name ?? item.product_name,
              article_code: npSummary.article_code ?? item.article_code,
              barcodes: npSummary.barcodes ?? item.barcodes,
              image_url: npSummary.image_url ?? item.image_url,
              new_product_source: npSummary.source ?? item.new_product_source,
              new_product_review_status: npSummary.review_status,
              new_product_rejection_reason: npSummary.rejection_reason ?? null,
              admin_product_id:
                npSummary.admin_product_id ?? item.admin_product_id,
              ...(rejected
                ? {
                    skipped_on_sync: true,
                    line_review_status: 'rejected',
                    line_rejection_reason:
                      npSummary.rejection_reason ||
                      'Rejected because the new product request was rejected',
                  }
                : {}),
            }
          }),
          new_products: (prev.new_products || []).map((np) =>
            np.id === npId ? { ...np, ...npSummary } : np,
          ),
        })
      })
    },
    [withReviewCounts],
  )

  const handleBulkApproveSelected = async () => {
    const ids = selectedEligibleLineItemIds
    if (ids.length === 0) return
    try {
      setSubmitting(true)
      setError(null)
      setLineItemBulkErrors({})
      const result = await inventorySyncAdminService.bulkApproveLineItems(syncId, ids)
      applyBulkApproveResult(result)

      const nextErrors = {}
      ;(result.failed || []).forEach((failure) => {
        if (failure?.line_item_id && failure?.message) {
          nextErrors[failure.line_item_id] = failure.message
        }
      })
      setLineItemBulkErrors(nextErrors)

      removeLineItemsFromSelection((result.updated || []).map((item) => item.id))

      const successCount = result.updated?.length ?? 0
      const failedCount = result.failed?.length ?? 0
      if (failedCount > 0 && successCount > 0) {
        setActionMessage({
          severity: 'warning',
          text: `Approved ${successCount} line item(s). ${failedCount} failed — see errors below the affected products.`,
        })
      } else if (failedCount > 0) {
        setActionMessage({
          severity: 'error',
          text: `${failedCount} line item(s) could not be approved — see errors below the affected products.`,
        })
      } else {
        clearLineItemSelection()
        setActionMessage({
          severity: 'success',
          text: `Approved ${successCount} line item(s).`,
        })
      }
    } catch (err) {
      console.error('Bulk approve failed', err)
      setError(getApiErrorMessage(err, 'Failed to approve selected line items'))
    } finally {
      setSubmitting(false)
    }
  }

  const pendingLineItemsForApprovalCount = useMemo(
    () => allItems.filter(isPendingLineItemForApproval).length,
    [allItems],
  )

  const pendingLineItemIdsForApproval = useMemo(
    () =>
      allItems
        .filter(isPendingLineItemForApproval)
        .map((item) => item.id)
        .filter(Boolean),
    [allItems],
  )

  const approveAllRunning = approveAllProgress?.status === 'running'

  const closeApproveAllProgress = () => {
    setApproveAllProgress(null)
  }

  const handleApproveAllLineItems = async () => {
    const pendingIds = pendingLineItemIdsForApproval
    if (pendingIds.length === 0) return

    const batches = chunkArray(pendingIds, APPROVE_ALL_BATCH_SIZE)

    setApproveAllDialogOpen(false)
    setSubmitting(true)
    setError(null)
    setLineItemBulkErrors({})
    clearLineItemSelection()

    let totalApproved = 0
    let totalFailed = 0
    let newProductsApproved = 0
    let processed = 0
    const nextErrors = {}

    setApproveAllProgress({
      status: 'running',
      processed: 0,
      total: pendingIds.length,
      approved: 0,
      failed: 0,
      batch: 0,
      batchTotal: batches.length,
    })

    try {
      for (let i = 0; i < batches.length; i += 1) {
        const batch = batches[i]
        setApproveAllProgress((prev) => ({
          ...prev,
          status: 'running',
          batch: i + 1,
        }))

        const result = await inventorySyncAdminService.bulkApproveLineItems(syncId, batch)
        applyBulkApproveResult(result)

        totalApproved += result.updated?.length ?? 0
        totalFailed += result.failed?.length ?? 0
        newProductsApproved += result.new_products_updated?.length ?? 0
        processed += batch.length

        ;(result.failed || []).forEach((failure) => {
          if (failure?.line_item_id && failure?.message) {
            nextErrors[failure.line_item_id] = failure.message
          }
        })

        setApproveAllProgress({
          status: 'running',
          processed,
          total: pendingIds.length,
          approved: totalApproved,
          failed: totalFailed,
          batch: i + 1,
          batchTotal: batches.length,
        })
      }

      setLineItemBulkErrors(nextErrors)
      await fetchSync({ resetView: false, showLoading: false })

      const finalStatus = totalFailed > 0 ? 'partial' : 'success'
      setApproveAllProgress({
        status: finalStatus,
        processed: pendingIds.length,
        total: pendingIds.length,
        approved: totalApproved,
        failed: totalFailed,
        batch: batches.length,
        batchTotal: batches.length,
      })

      if (totalFailed > 0) {
        setActionMessage({
          severity: 'warning',
          text: `Approved ${totalApproved} line item(s)${
            newProductsApproved > 0
              ? ` (${newProductsApproved} new product(s) created)`
              : ''
          }. ${totalFailed} could not be approved — highlighted below.`,
        })
      } else {
        setActionMessage({
          severity: 'success',
          text: `Approved all ${totalApproved} pending line item(s)${
            newProductsApproved > 0 ? ` and ${newProductsApproved} new product(s)` : ''
          }. You can now approve the sync when ready.`,
        })
        window.setTimeout(() => {
          setApproveAllProgress(null)
        }, 1200)
      }
    } catch (err) {
      console.error('Approve all line items failed', err)
      setApproveAllProgress(null)
      setError(getApiErrorMessage(err, 'Failed to approve all line items'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleBulkRejectSelected = async () => {
    const ids = selectedEligibleLineItemIds
    if (ids.length === 0) return
    try {
      setSubmitting(true)
      setError(null)
      const result = await inventorySyncAdminService.bulkRejectLineItems(
        syncId,
        ids,
        bulkRejectReason || undefined,
      )
      applyBulkLineItemUpdates(result.updated)
      setBulkRejectDialogOpen(false)
      setBulkRejectReason('')
      clearLineItemSelection()
      setActionMessage({
        severity: 'info',
        text: `Rejected ${result.updated?.length ?? 0} line item(s).`,
      })
    } catch (err) {
      console.error('Bulk reject failed', err)
      setError(err.response?.data?.message || 'Failed to reject selected line items')
    } finally {
      setSubmitting(false)
    }
  }

  const handleBulkUpdateNonShwapno = async (isNonShwapno) => {
    const ids = selectedEligibleLineItemIds
    if (ids.length === 0) return
    try {
      setSubmitting(true)
      setError(null)
      const result = await inventorySyncAdminService.bulkUpdateLineItemsNonShwapno(
        syncId,
        ids,
        isNonShwapno,
      )
      applyBulkLineItemUpdates(result.updated)

      const successCount = result.updated?.length ?? 0
      const failedCount = result.failed?.length ?? 0
      const label = isNonShwapno ? 'Non-Shwapno (Yes)' : 'Shwapno (No)'
      if (failedCount > 0 && successCount > 0) {
        removeLineItemsFromSelection((result.updated || []).map((item) => item.id))
        setActionMessage({
          severity: 'warning',
          text: `Marked ${successCount} line item(s) as ${label}. ${failedCount} failed.`,
        })
      } else if (failedCount > 0) {
        setActionMessage({
          severity: 'error',
          text: `${failedCount} line item(s) could not be marked as ${label}.`,
        })
      } else {
        clearLineItemSelection()
        setActionMessage({
          severity: 'success',
          text: `Marked ${successCount} line item(s) as ${label}.`,
        })
      }
    } catch (err) {
      console.error('Bulk Non-Shwapno update failed', err)
      setError(
        getApiErrorMessage(
          err,
          isNonShwapno
            ? 'Failed to mark selected products as Non-Shwapno'
            : 'Failed to mark selected products as Shwapno',
        ),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const handleApproveLineItem = async (lineItemId) => {
    try {
      setSubmitting(true)
      setError(null)
      const updated = await inventorySyncAdminService.approveLineItem(syncId, lineItemId)
      updateItemInState(updated)
      removeLineItemsFromSelection([lineItemId])
      setActionMessage({ severity: 'success', text: 'Line item approved.' })
    } catch (err) {
      console.error('Line approve failed', err)
      setError(err.response?.data?.message || 'Failed to approve line item')
    } finally {
      setSubmitting(false)
    }
  }

  const handleRejectLineItem = async (lineItemId, reason) => {
    try {
      setSubmitting(true)
      setError(null)
      const updated = await inventorySyncAdminService.rejectLineItem(
        syncId,
        lineItemId,
        reason,
      )
      updateItemInState(updated)
      removeLineItemsFromSelection([lineItemId])
      setActionMessage({ severity: 'info', text: 'Line item rejected.' })
    } catch (err) {
      console.error('Line reject failed', err)
      setError(err.response?.data?.message || 'Failed to reject line item')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDownloadSampleCsv = () => {
    const header = [
      'Line Item No.',
      'Article Code',
      'Product Name',
      'Quantity',
      'Selling Price',
      'MRP',
      'Expiry',
      'Non-Shwapno',
    ]
    const rows = allItems.map((item) => [
      csvEscape(item.id),
      csvEscape(item.article_code || ''),
      csvEscape(item.product_name || ''),
      csvEscape(item.quantity ?? ''),
      csvEscape(Number(item.unit_cost || 0).toFixed(2)),
      csvEscape(Number(item.unit_price || 0).toFixed(2)),
      csvEscape(toEditableDate(item.expiry_date)),
      csvEscape(item.is_non_shwapno ? 'yes' : 'no'),
    ])
    const csv = [header.join(','), ...rows.map((row) => row.join(','))].join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `inventory-sync-${syncId}-sample.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  const handleCsvFileSelect = (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setSelectedCsvFile(file)
    setCsvResult(null)
  }

  const handleUploadCsv = async () => {
    if (!selectedCsvFile) {
      setError('Please choose a CSV file before uploading')
      return
    }
    try {
      setUploadingCsv(true)
      setCsvResult(null)
      setError(null)
      const response = await inventorySyncAdminService.importLineItemsCsv(syncId, selectedCsvFile)
      setCsvResult(response)
      setSelectedCsvFile(null)
      await fetchSync({ resetView: false, showLoading: false })
      setActionMessage({
        severity: 'success',
        text: `CSV processed. Updated ${response.updated || 0} line item(s).`,
      })
    } catch (err) {
      console.error('CSV upload failed', err)
      setError(err.response?.data?.message || 'Failed to upload CSV')
    } finally {
      setUploadingCsv(false)
    }
  }

  const resetBulkLinkDialogState = () => {
    setSelectedBulkLinkCsvFile(null)
    setBulkLinkPreview(null)
    setBulkLinkConfirmResult(null)
  }

  const handleDownloadBulkLinkLineItemsCsv = () => {
    const header = [
      'Line Item No.',
      'Article Code',
      'Product Name',
      'Quantity',
      'Selling Price',
      'MRP',
      'Expiry Date',
      'External Article Code',
    ]
    const rows = bulkLinkEligibleItems.map((item) => [
      csvEscape(item.id),
      csvEscape(item.article_code || ''),
      csvEscape(item.product_name || ''),
      csvEscape(item.quantity ?? ''),
      csvEscape(Number(item.unit_cost || 0).toFixed(2)),
      csvEscape(Number(item.unit_price || 0).toFixed(2)),
      csvEscape(toEditableDate(item.expiry_date)),
      '',
    ])
    const csv = [header.join(','), ...rows.map((row) => row.join(','))].join('\r\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `inventory-sync-${syncId}-bulk-link-line-items.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  const handleBulkLinkCsvFileSelect = (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return
    setSelectedBulkLinkCsvFile(file)
    setBulkLinkPreview(null)
    setBulkLinkConfirmResult(null)
  }

  const handleValidateBulkLinkCsv = async () => {
    if (!selectedBulkLinkCsvFile) {
      setError('Please choose a CSV file before validating')
      return
    }
    try {
      setBulkLinkUploading(true)
      setBulkLinkPreview(null)
      setBulkLinkConfirmResult(null)
      setError(null)
      const response = await inventorySyncAdminService.previewLinkProductsCsv(
        syncId,
        selectedBulkLinkCsvFile,
      )
      setBulkLinkPreview(response)
    } catch (err) {
      console.error('Bulk link CSV validation failed', err)
      setError(err.response?.data?.message || 'Failed to validate bulk link CSV')
    } finally {
      setBulkLinkUploading(false)
    }
  }

  const handleConfirmBulkLink = async () => {
    if (!bulkLinkPreview?.ready?.length) return
    try {
      setBulkLinkConfirming(true)
      setError(null)
      const items = bulkLinkPreview.ready.map((row) => ({
        line_item_id: row.line_item_id,
        admin_product_id: row.admin_product_id,
        quantity: row.quantity,
        unit_cost: row.cost_price,
        unit_price: row.mrp,
        expiry_date: row.expiry_date ?? null,
        product_name: row.product_name,
        article_code: row.article_code || undefined,
      }))
      const response = await inventorySyncAdminService.confirmLinkProductsCsv(syncId, items)
      setBulkLinkConfirmResult(response)
      setBulkLinkPreview(null)
      setSelectedBulkLinkCsvFile(null)
      await fetchSync({ resetView: false, showLoading: false })
      setActionMessage({
        severity: 'success',
        text: `Bulk link confirmed. Updated ${response.updated || 0}, linked ${response.linked || 0} line item(s).`,
      })
    } catch (err) {
      console.error('Bulk link confirm failed', err)
      setError(err.response?.data?.message || 'Failed to confirm bulk link')
    } finally {
      setBulkLinkConfirming(false)
    }
  }

  const openSkuConflictPrompt = (parsed, fallbackNewProductId) => {
    setSkuConflictPrompt({
      newProductId: parsed.newProductId || fallbackNewProductId,
      sku: parsed.sku || parsed.existingProduct?.sku || '',
      message: parsed.message,
      existingProduct: parsed.existingProduct,
    })
    setApproveNewProductId(null)
    setApproveNewProductError(null)
  }

  const handleApproveNewProduct = async (newProductId) => {
    try {
      setSubmitting(true)
      setError(null)
      setApproveNewProductError(null)
      const npSummary = await inventorySyncAdminService.approveNewProduct(
        syncId,
        newProductId,
      )
      applyNewProductReviewInState(npSummary)
      setApproveNewProductId(null)
      setActionMessage({
        severity: 'success',
        text: 'New product approved and catalog entry created.',
      })
    } catch (err) {
      console.error('Approve new product failed', err)
      const parsed = parseInventorySyncApiError(err)
      if (isSkuAlreadyExistsError(parsed)) {
        openSkuConflictPrompt(parsed, newProductId)
        return
      }
      // Keep the dialog open so the user always sees line-item failures
      // (e.g. barcode already assigned) instead of a top-of-page alert.
      setApproveNewProductError(parsed.message || 'Failed to approve new product')
    } finally {
      setSubmitting(false)
    }
  }

  const handleConfirmLinkMatchingSku = async () => {
    if (!skuConflictPrompt?.newProductId || !skuConflictPrompt?.existingProduct?.id) return
    try {
      setSkuConflictSubmitting(true)
      setError(null)
      await inventorySyncAdminService.linkNewProduct(
        syncId,
        skuConflictPrompt.newProductId,
        skuConflictPrompt.existingProduct.id,
      )
      const label =
        skuConflictPrompt.existingProduct.product_name ||
        skuConflictPrompt.existingProduct.sku ||
        'catalog product'
      setSkuConflictPrompt(null)
      setRowEditOpen(false)
      setEditingItem(null)
      setActionMessage({
        severity: 'success',
        text: `Linked and approved using existing product: ${label}`,
      })
      await fetchSync()
    } catch (err) {
      console.error('Link matching SKU product failed', err)
      setError(getApiErrorMessage(err, 'Failed to link to existing SKU product'))
      setSkuConflictPrompt(null)
    } finally {
      setSkuConflictSubmitting(false)
    }
  }

  const handleSkuConflictLinkDifferent = () => {
    if (!skuConflictPrompt?.newProductId) return
    const newProductId = skuConflictPrompt.newProductId
    setSkuConflictPrompt(null)
    openEditForNewProduct(newProductId)
    setSelectedLinkProduct(null)
    setLinkProductSearch('')
    setLinkDialogError(null)
    setLinkDialogOpen(true)
  }

  const handleRejectNewProductConfirm = async () => {
    if (!rejectNewProductId) return
    try {
      setSubmitting(true)
      setError(null)
      const npSummary = await inventorySyncAdminService.rejectNewProduct(
        syncId,
        rejectNewProductId,
        rejectNewProductReason || undefined,
      )
      applyNewProductReviewInState(npSummary)
      setRejectNewProductId(null)
      setRejectNewProductReason('')
      setActionMessage({ severity: 'info', text: 'New product rejected.' })
    } catch (err) {
      console.error('Reject new product failed', err)
      setError(err.response?.data?.message || 'Failed to reject new product')
    } finally {
      setSubmitting(false)
    }
  }

  const handleOpenEdit = (item) => {
    setEditingItem(item)
    setRowEditError(null)
    const imageUrl =
      item.image_url && typeof item.image_url === 'object'
        ? item.image_url.logo || item.image_url.portrait || ''
        : typeof item.image_url === 'string'
          ? item.image_url
          : ''
    setRowForm({
      quantity: item.quantity ?? '',
      unit_cost: item.unit_cost ?? '',
      unit_price: item.unit_price ?? '',
      expiry_date: toEditableDate(item.expiry_date),
      is_non_shwapno: !!item.is_non_shwapno,
      product_name: item.product_name ?? '',
      article_code: item.article_code ?? '',
      barcodes: barcodesFromItem(item),
      image_url: imageUrl,
    })
    setBarcodeInput('')
    setRowEditOpen(true)
  }

  const openEditForNewProduct = (newProductId) => {
    const lineItem =
      allItems.find(
        (item) =>
          item.inventory_sync_new_product_id === newProductId &&
          primaryLineIdByNewProduct.get(newProductId) === item.id,
      ) || allItems.find((item) => item.inventory_sync_new_product_id === newProductId)
    if (lineItem) {
      handleOpenEdit(lineItem)
    }
  }

  const addBarcodeToForm = () => {
    const next = String(barcodeInput || '').trim()
    if (!next) return
    if (rowForm.barcodes.some((b) => String(b).trim() === next)) return
    setRowForm((prev) => ({ ...prev, barcodes: [...prev.barcodes, next] }))
    setBarcodeInput('')
  }

  const removeBarcodeFromForm = (value) => {
    setRowForm((prev) => ({
      ...prev,
      barcodes: prev.barcodes.filter((b) => b !== value),
    }))
  }

  const buildRowEditPayload = () => {
    const payload = {
      quantity: Number(rowForm.quantity),
      unit_cost: Number(rowForm.unit_cost),
      unit_price: Number(rowForm.unit_price),
      is_non_shwapno: !!rowForm.is_non_shwapno,
      ...(rowForm.expiry_date ? { expiry_date: rowForm.expiry_date } : {}),
    }
    if (editingItem?.line_type === 'new') {
      if (rowForm.product_name) payload.product_name = rowForm.product_name
      if (
        rowForm.article_code !== undefined &&
        rowForm.article_code !== ''
      ) {
        payload.article_code = rowForm.article_code
      } else if (editingItem.new_product_source !== 'external') {
        payload.article_code = rowForm.article_code
      }
      payload.barcodes = rowForm.barcodes
        .map((b) => String(b).trim())
        .filter(Boolean)
      if (rowForm.image_url) {
        payload.image_url = { logo: rowForm.image_url }
      }
    }
    return payload
  }

  const saveEditingRow = async () => {
    if (!editingItem) return null
    const updated = await inventorySyncAdminService.updateLineItem(
      syncId,
      editingItem.id,
      buildRowEditPayload(),
    )
    updateItemInState(updated)
    return updated
  }

  const handleOpenLinkDialog = () => {
    setSelectedLinkProduct(null)
    setLinkProductSearch('')
    setLinkDialogError(null)
    setLinkDialogOpen(true)
  }

  const handleLinkToExistingProduct = async () => {
    if (!editingItem?.inventory_sync_new_product_id || !selectedLinkProduct?.id) return
    try {
      setLinkSubmitting(true)
      setError(null)
      setLinkDialogError(null)
      await saveEditingRow()
      const npSummary = await inventorySyncAdminService.linkNewProduct(
        syncId,
        editingItem.inventory_sync_new_product_id,
        selectedLinkProduct.id,
      )
      applyNewProductReviewInState(npSummary)
      setLinkDialogOpen(false)
      setRowEditOpen(false)
      setEditingItem(null)
      setActionMessage({
        severity: 'success',
        text: `Linked to existing product: ${catalogProductLabel(selectedLinkProduct)}`,
      })
    } catch (err) {
      console.error('Link product failed', err)
      setLinkDialogError(getApiErrorMessage(err, 'Failed to link to existing product'))
    } finally {
      setLinkSubmitting(false)
    }
  }

  const handleCreateAsNewInternalProduct = async () => {
    if (!editingItem?.inventory_sync_new_product_id) return
    const isExternal = editingItem.new_product_source === 'external'
    const confirmed = window.confirm(
      isExternal
        ? rowForm.article_code?.trim() || editingItem.article_code?.trim()
          ? `Create this external product using identifier "${rowForm.article_code?.trim() || editingItem.article_code}"?`
          : 'Create this external product? A new identifier (9xxxxxx) will be assigned automatically.'
        : 'Create this as a new internal catalog product? Ensure identifier and product details are correct before continuing.',
    )
    if (!confirmed) return
    try {
      setCreateInternalSubmitting(true)
      setError(null)
      setRowEditError(null)
      await saveEditingRow()
      const npSummary = await inventorySyncAdminService.approveNewProduct(
        syncId,
        editingItem.inventory_sync_new_product_id,
      )
      applyNewProductReviewInState(npSummary)
      setRowEditOpen(false)
      setEditingItem(null)
      setActionMessage({
        severity: 'success',
        text: 'New internal catalog product created and linked to this sync request.',
      })
    } catch (err) {
      console.error('Create internal product failed', err)
      const parsed = parseInventorySyncApiError(err)
      if (isSkuAlreadyExistsError(parsed)) {
        openSkuConflictPrompt(
          parsed,
          editingItem.inventory_sync_new_product_id,
        )
        return
      }
      setRowEditError(parsed.message || 'Failed to create new internal product')
    } finally {
      setCreateInternalSubmitting(false)
    }
  }

  const handleSaveRow = async () => {
    if (!editingItem) return
    const cost = Number(rowForm.unit_cost)
    const price = Number(rowForm.unit_price)
    const priceError = validatePositiveSellingPriceAndMrp(cost, price, {
      requireBoth: true,
    })
    if (priceError) {
      setRowEditError(priceError)
      return
    }
    try {
      setRowEditSaving(true)
      setError(null)
      setRowEditError(null)
      await saveEditingRow()
      setRowEditOpen(false)
      setEditingItem(null)
      setActionMessage({ severity: 'success', text: 'Line item updated.' })
    } catch (err) {
      console.error('Row update failed', err)
      setRowEditError(getApiErrorMessage(err, 'Failed to update line item'))
    } finally {
      setRowEditSaving(false)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!sync) {
    return (
      <Box>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/inventory-sync')}>
          Back
        </Button>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error || 'Inventory sync not found'}
        </Alert>
      </Box>
    )
  }

  const isPendingApproval = sync.status === 'submitted'
  const pendingLineItemsCount = sync.pending_line_items_count ?? 0
  const approvedLineItemsCount = allItems.filter(
    (item) => getLineReviewStatus(item) === 'approved',
  ).length
  const allLineItemsRejected =
    allItems.length > 0 &&
    pendingLineItemsCount === 0 &&
    approvedLineItemsCount === 0
  const canApproveSync =
    sync.can_approve_sync !== false && !allLineItemsRejected
  const totalQuantity = allItems.reduce((acc, item) => acc + Number(item.quantity || 0), 0)
  const approveNewProductPreview = approveNewProductId
    ? allItems.find((item) => item.inventory_sync_new_product_id === approveNewProductId)
    : null
  const rejectionReason =
    sync.status === 'rejected' && sync.meta && typeof sync.meta === 'object'
      ? sync.meta.reason
      : null

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
        <IconButton onClick={() => navigate('/inventory-sync')}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {sync.name?.trim() ? sync.name.trim() : 'Inventory Sync Detail'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
            {sync.name?.trim()
              ? `Sync #${sync.id}`
              : `Inventory Sync #${sync.id}`}
            {' · '}
            Initiated by{' '}
            {sync.initiated_by === 'admin' || sync.meta?.initiated_by === 'admin'
              ? 'Shwapno admin'
              : 'store'}
          </Typography>
        </Box>
        <Chip
          label={STATUS_LABELS[sync.status] || sync.status}
          color={getStatusChipColor(sync.status)}
          variant="outlined"
        />
        {sync.status !== 'approved' && sync.status !== 'reverted' && (
          <IconButton
            color="error"
            title="Delete sync"
            onClick={() => setDeleteDialogOpen(true)}
            disabled={deleting}
          >
            <DeleteIcon />
          </IconButton>
        )}
        {sync.status === 'approved' && (
          <Button
            variant="outlined"
            color="warning"
            startIcon={<UndoIcon />}
            onClick={openRevertDialog}
            disabled={reverting || !!revertProgress}
          >
            Revert
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {revertProgress && (
        <Alert severity="info" sx={{ mb: 2 }} icon={<CircularProgress size={18} />}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {revertProgress.message || 'Reverting inventory sync…'}
          </Typography>
          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
            This can take a minute for large syncs. Keep this page open.
          </Typography>
          <LinearProgress sx={{ mt: 1.5 }} />
        </Alert>
      )}
      {actionMessage && (
        <Alert
          severity={actionMessage.severity}
          sx={{ mb: 2 }}
          onClose={() => setActionMessage(null)}
        >
          {actionMessage.text}
        </Alert>
      )}
      {rejectionReason ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Rejection reason
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {String(rejectionReason)}
          </Typography>
        </Alert>
      ) : null}

      <Card sx={{ mb: 3, p: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
          <Box>
            <Typography variant="caption" sx={{ color: '#888' }}>
              Sync name
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {sync.name?.trim() || '-'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#888' }}>
              ID #{sync.id}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#888' }}>
              Shop Id
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {sync.org_name || '-'}{' '}
              <Typography component="span" variant="caption" sx={{ color: '#888' }}>
                (Shop Id {sync.org_id ?? '-'})
              </Typography>
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#888' }}>
              Items
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {allItems.length} ({totalQuantity} units)
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#888' }}>
              Created
            </Typography>
            <Typography variant="body1">{formatDate(sync.created_at)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: '#888' }}>
              Updated
            </Typography>
            <Typography variant="body1">{formatDate(sync.updated_at)}</Typography>
          </Box>
        </Stack>
      </Card>

      <Box sx={{ mb: 1 }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => {
            setActiveTab(v)
            if (v === 'audit') fetchAuditLog()
          }}
        >
          <Tab value="line-items" label="Line Items" />
          <Tab value="audit" label="Audit Log" icon={<HistoryIcon sx={{ fontSize: 16 }} />} iconPosition="start" />
        </Tabs>
      </Box>

      {activeTab === 'audit' && (
        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)', p: 2 }}>
          {auditLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : !auditLog || auditLog.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No audit events recorded yet.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Action</TableCell>
                    <TableCell>Actor</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {auditLog.map((entry) => {
                    const auditChipColor =
                      entry.action === 'APPROVED' ? 'success'
                      : entry.action === 'REVERTED' ? 'warning'
                      : (entry.action === 'REJECTED' || entry.action === 'DELETED') ? 'error'
                      : 'default'
                    return (
                    <TableRow key={entry.id}>
                      <TableCell>
                        <Chip
                          label={entry.action}
                          size="small"
                          color={auditChipColor}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {entry.performed_by_email || entry.performed_by || '—'}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 320, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {entry.notes || '—'}
                      </TableCell>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>
                        {entry.created_at
                          ? new Date(entry.created_at).toLocaleString()
                          : '—'}
                      </TableCell>
                    </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Card>
      )}

      {activeTab === 'line-items' && (
        <Card sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid #eee',
            display: 'flex',
            alignItems: { xs: 'stretch', sm: 'center' },
            gap: 1,
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', sm: 'row' },
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 600, flexGrow: 1, minWidth: { sm: 120 } }}
          >
            Line Items
          </Typography>
          {allItems.length > 0 && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                width: { xs: '100%', md: 'auto' },
                '& .MuiButton-root': {
                  flex: { xs: '1 1 calc(50% - 4px)', sm: '0 1 auto' },
                  whiteSpace: 'nowrap',
                },
              }}
            >
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleExportCsv}
                disabled={exporting}
              >
                {exporting ? 'Exporting…' : 'Export CSV'}
              </Button>
              {isPendingApproval && (
                <Button
                  variant="contained"
                  size="small"
                  color="warning"
                  startIcon={<EditIcon />}
                  onClick={() => setModifyDialogOpen(true)}
                >
                  Modified
                </Button>
              )}
              {isPendingApproval && bulkLinkEligibleItems.length > 0 && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<LinkIcon />}
                  onClick={() => {
                    resetBulkLinkDialogState()
                    setBulkLinkDialogOpen(true)
                  }}
                >
                  <SelectionActionLabel
                    shortLabel="Bulk Link"
                    fullLabel="Bulk Link Products"
                  />
                </Button>
              )}
            </Box>
          )}
          {isPendingApproval && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              useFlexGap
              sx={{
                width: { xs: '100%', md: 'auto' },
                flexWrap: 'wrap',
                '& .MuiButton-root': {
                  width: { xs: '100%', sm: 'auto' },
                  whiteSpace: 'nowrap',
                },
              }}
            >
              {pendingLineItemsForApprovalCount > 0 ? (
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<DoneAllIcon />}
                  onClick={() => setApproveAllDialogOpen(true)}
                  disabled={submitting || approveAllRunning}
                  sx={approveButtonSx}
                >
                  <SelectionActionLabel
                    shortLabel={`Approve All (${pendingLineItemsForApprovalCount})`}
                    fullLabel={`Approve All Line Items (${pendingLineItemsForApprovalCount})`}
                  />
                </Button>
              ) : null}
              <Button
                variant="contained"
                color="error"
                onClick={() => setRejectDialogOpen(true)}
                disabled={submitting}
              >
                Reject
              </Button>
              <Button
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                onClick={() => setApproveDialogOpen(true)}
                disabled={submitting || !canApproveSync}
                sx={approveButtonSx}
              >
                Approve
              </Button>
            </Stack>
          )}
        </Box>
        <Box sx={{ px: 2, borderBottom: '1px solid #eee' }}>
          <Tabs
            value={lineItemSection}
            onChange={(_, value) => {
              setLineItemSection(value)
              setItemPage(0)
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 44,
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 44,
              },
            }}
          >
            <Tab
              value="all"
              label={`${LINE_ITEM_SECTIONS.all.label} (${lineItemSectionCounts.all})`}
            />
            <Tab
              value="internal"
              label={`${LINE_ITEM_SECTIONS.internal.label} (${lineItemSectionCounts.internal})`}
            />
            <Tab
              value="external"
              label={`${LINE_ITEM_SECTIONS.external.label} (${lineItemSectionCounts.external})`}
            />
          </Tabs>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
              py: 1.25,
            }}
          >
            <Typography variant="body2" sx={{ color: '#666', flex: '1 1 220px' }}>
              {LINE_ITEM_SECTIONS[lineItemSection]?.description}
              {lineItemSearchQuery.trim() ? (
                <>
                  {' '}
                  · {searchFilteredItems.length} match
                  {searchFilteredItems.length !== 1 ? 'es' : ''} for &ldquo;
                  {lineItemSearchQuery.trim()}&rdquo;
                </>
              ) : null}
            </Typography>
            <TextField
              size="small"
              placeholder="Search by product name or article code"
              value={lineItemSearchQuery}
              onChange={(e) => {
                setLineItemSearchQuery(e.target.value)
                setItemPage(0)
              }}
              sx={{ minWidth: { xs: '100%', sm: 280 }, flex: '1 1 280px', maxWidth: 420 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: lineItemSearchQuery ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      aria-label="Clear product search"
                      onClick={() => {
                        setLineItemSearchQuery('')
                        setItemPage(0)
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="line-review-status-filter-label">Line status</InputLabel>
              <Select
                labelId="line-review-status-filter-label"
                label="Line status"
                value={lineReviewStatusFilter}
                onChange={(e) => {
                  setLineReviewStatusFilter(e.target.value)
                  setItemPage(0)
                }}
              >
                <MenuItem value="all">
                  {LINE_REVIEW_STATUS_FILTERS.all.label} ({lineReviewStatusCounts.all})
                </MenuItem>
                <MenuItem value="pending">
                  {LINE_REVIEW_STATUS_FILTERS.pending.label} ({lineReviewStatusCounts.pending})
                </MenuItem>
                <MenuItem value="approved">
                  {LINE_REVIEW_STATUS_FILTERS.approved.label} ({lineReviewStatusCounts.approved})
                </MenuItem>
                <MenuItem value="rejected">
                  {LINE_REVIEW_STATUS_FILTERS.rejected.label} ({lineReviewStatusCounts.rejected})
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
        {lineItemsPagination}
        {isPendingApproval && pendingLineItemsCount > 0 ? (
          <Alert severity="warning" sx={{ mx: 2, mt: 2 }}>
            {pendingLineItemsCount} line item(s) still need review (approve or reject each
            line before approving the sync, or use Approve All Line Items to approve every
            pending line — already rejected lines are skipped).
          </Alert>
        ) : null}
        {isPendingApproval && allLineItemsRejected ? (
          <Alert severity="warning" sx={{ mx: 2, mt: 2 }}>
            All line items are rejected, so this sync cannot be approved. Reject the sync
            instead, or approve at least one line item first.
          </Alert>
        ) : null}
        {isPendingApproval && selectedCount > 0 ? (
          <Box
            sx={{
              px: { xs: 1.5, sm: 2 },
              py: 1.5,
              borderBottom: '1px solid #eee',
              display: 'flex',
              alignItems: { xs: 'stretch', sm: 'center' },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 1,
              flexWrap: 'wrap',
              bgcolor: '#f8fafc',
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, flexShrink: 0, pt: { xs: 0, sm: 0.5 } }}
            >
              {selectedCount} selected
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
              }}
            >
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={submitting}
                onClick={handleBulkApproveSelected}
                sx={{ ...approveButtonSx, ...selectionActionButtonSx }}
              >
                <SelectionActionLabel
                  shortLabel="Approve"
                  fullLabel="Approve Selected"
                />
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                disabled={submitting}
                onClick={() => setBulkRejectDialogOpen(true)}
                sx={selectionActionButtonSx}
              >
                <SelectionActionLabel
                  shortLabel="Reject"
                  fullLabel="Reject Selected"
                />
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={submitting}
                onClick={() => handleBulkUpdateNonShwapno(true)}
                title="Mark products as Non-Shwapno (Yes)"
                aria-label="Mark products as Non-Shwapno (Yes)"
                sx={selectionActionButtonSx}
              >
                <SelectionActionLabel
                  shortLabel="Non-Shwapno (Yes)"
                  fullLabel="Mark products as Non-Shwapno (Yes)"
                />
              </Button>
              <Button
                size="small"
                variant="outlined"
                disabled={submitting}
                onClick={() => handleBulkUpdateNonShwapno(false)}
                title="Mark products as Shwapno (No)"
                aria-label="Mark products as Shwapno (No)"
                sx={selectionActionButtonSx}
              >
                <SelectionActionLabel
                  shortLabel="Shwapno (No)"
                  fullLabel="Mark products as Shwapno (No)"
                />
              </Button>
              <Button
                size="small"
                variant="text"
                onClick={clearLineItemSelection}
                disabled={submitting}
                sx={{
                  ...selectionActionButtonSx,
                  flex: { xs: '1 1 100%', sm: '0 1 auto' },
                }}
              >
                Clear Selection
              </Button>
            </Box>
          </Box>
        ) : null}
        {filteredItems.length > 0 && (
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8f9fb' }}>
                {isPendingApproval ? (
                  <TableCell padding="checkbox" sx={lineItemsTableHeaderCellSx}>
                    <Checkbox
                      indeterminate={someVisibleSelectableSelected}
                      checked={allVisibleSelectableSelected && selectablePagedItems.length > 0}
                      disabled={selectablePagedItems.length === 0 || submitting}
                      onChange={toggleSelectAllVisible}
                      inputProps={{ 'aria-label': 'Select all line items on this page' }}
                    />
                  </TableCell>
                ) : null}
                <TableCell sx={{ ...lineItemsTableHeaderCellSx, minWidth: 100 }}>Line Item ID</TableCell>
                <TableCell sx={{ ...lineItemsTableHeaderCellSx, minWidth: 120 }}>Identifier</TableCell>
                <TableCell sx={{ ...lineItemsTableHeaderCellSx, minWidth: 220 }}>Product</TableCell>
                <TableCell sx={lineItemsTableHeaderCellSx}>Line review</TableCell>
                <TableCell sx={lineItemsTableHeaderCellSx} align="right">
                  Quantity
                </TableCell>
                <TableCell sx={lineItemsTableHeaderCellSx} align="right">
                  Selling Price
                </TableCell>
                <TableCell sx={lineItemsTableHeaderCellSx} align="right">
                  MRP
                </TableCell>
                <TableCell sx={lineItemsTableHeaderCellSx}>Expiry</TableCell>
                <TableCell sx={lineItemsTableHeaderCellSx}>Non-Shwapno</TableCell>
                {isPendingApproval ? (
                  <TableCell sx={{ ...lineItemsTableHeaderCellSx, minWidth: 280 }} align="right">
                    Actions
                  </TableCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {pagedItems.map((item, rowIndex) => {
                const showNewProductActions =
                  item.line_type === 'new' &&
                  item.inventory_sync_new_product_id &&
                  item.new_product_review_status === 'pending' &&
                  primaryLineIdByNewProduct.get(item.inventory_sync_new_product_id) === item.id

                const canEditLineItem =
                  item.line_type !== 'new' ||
                  !item.inventory_sync_new_product_id ||
                  item.new_product_review_status === 'pending' ||
                  !item.new_product_review_status

                const lineReviewStatus = item.line_review_status || 'pending'
                const rowSelectable = isPendingApproval && isLineItemSelectable(item)
                const hasApproveAllError = !!lineItemBulkErrors[item.id]

                return (
                  <TableRow
                    key={item.id}
                    hover
                    selected={isLineItemIdSelected(item.id)}
                    sx={{
                      bgcolor: hasApproveAllError
                        ? '#fef2f2'
                        : rowIndex % 2 === 1
                          ? '#fafbfc'
                          : '#fff',
                      transition: 'background-color 0.2s ease',
                      '&:hover': {
                        bgcolor: hasApproveAllError ? '#fee2e2' : '#f8f9fb',
                      },
                      ...(hasApproveAllError
                        ? { boxShadow: 'inset 3px 0 0 #dc2626' }
                        : {}),
                      '&:last-child td': { borderBottom: 0 },
                    }}
                  >
                    {isPendingApproval ? (
                      <TableCell padding="checkbox" sx={lineItemsTableBodyCellSx}>
                        <Checkbox
                          checked={isLineItemIdSelected(item.id)}
                          disabled={!rowSelectable || submitting}
                          onChange={() => toggleLineItemSelected(item.id)}
                          inputProps={{ 'aria-label': `Select line item ${item.id}` }}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell
                      sx={{
                        ...lineItemsTableBodyCellSx,
                        minWidth: 100,
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        color: '#666',
                      }}
                    >
                      {item.id}
                    </TableCell>
                    <TableCell
                      sx={{
                        ...lineItemsTableBodyCellSx,
                        minWidth: 120,
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        color: '#666',
                      }}
                    >
                      {item.article_code || '-'}
                    </TableCell>
                    <TableCell sx={{ ...lineItemsTableBodyCellSx, minWidth: 220 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: '#1a1a1a', lineHeight: 1.4 }}>
                        {item.product_name || '-'}
                      </Typography>
                      <LineItemBarcodes item={item} />
                      {getLineOriginLabel(item) ? (
                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', mt: 0.75, gap: 0.5 }}>
                          {item.line_type === 'new' ? (
                            <Chip size="small" label="New" color="warning" />
                          ) : null}
                          <Chip
                            size="small"
                            label={getLineOriginLabel(item)}
                            variant="outlined"
                          />
                          {item.line_type === 'new' && item.new_product_review_status ? (
                            <Chip
                              size="small"
                              label={item.new_product_review_status}
                              color={getNewProductReviewChipColor(item.new_product_review_status)}
                            />
                          ) : null}
                        </Stack>
                      ) : null}
                      {item.new_product_review_status === 'rejected' &&
                      item.new_product_rejection_reason ? (
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ color: '#b91c1c', mt: 0.75, lineHeight: 1.4 }}
                        >
                          Rejection reason: {item.new_product_rejection_reason}
                        </Typography>
                      ) : null}
                      {item.skipped_on_sync ? (
                        <Typography variant="caption" display="block" sx={{ color: '#b45309', mt: 0.5 }}>
                          Skipped on sync
                        </Typography>
                      ) : null}
                      {lineItemBulkErrors[item.id] ? (
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ color: '#b91c1c', mt: 0.75, lineHeight: 1.4, fontWeight: 500 }}
                        >
                          {lineItemBulkErrors[item.id]}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={lineItemsTableBodyCellSx}>
                      <Chip
                        size="small"
                        label={formatLineReviewLabel(lineReviewStatus)}
                        color={getLineReviewChipColor(lineReviewStatus)}
                      />
                      {lineReviewStatus === 'rejected' && item.line_rejection_reason ? (
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ color: '#b91c1c', mt: 0.75, lineHeight: 1.4 }}
                        >
                          {item.line_rejection_reason}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell align="right" sx={lineItemsTableNumericCellSx}>
                      {item.quantity}
                    </TableCell>
                    <TableCell align="right" sx={lineItemsTableNumericCellSx}>
                      {formatCurrency(item.unit_cost)}
                    </TableCell>
                    <TableCell align="right" sx={lineItemsTableNumericCellSx}>
                      {formatCurrency(item.unit_price)}
                    </TableCell>
                    <TableCell sx={lineItemsTableBodyCellSx}>{formatDateOnly(item.expiry_date)}</TableCell>
                    <TableCell sx={lineItemsTableBodyCellSx}>
                      <Chip
                        size="small"
                        label={item.is_non_shwapno ? 'Yes' : 'No'}
                        color={item.is_non_shwapno ? 'warning' : 'default'}
                        variant={item.is_non_shwapno ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    {isPendingApproval ? (
                      <TableCell align="right" sx={{ ...lineItemsTableBodyCellSx, minWidth: 280 }}>
                        <Stack
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          justifyContent="flex-end"
                          flexWrap="wrap"
                          useFlexGap
                          sx={{ gap: 1 }}
                        >
                          {canEditLineItem ? (
                            <Button
                              size="small"
                              variant="outlined"
                              color="inherit"
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenEdit(item)}
                              sx={{
                                ...lineItemActionButtonSx,
                                color: '#666',
                                borderColor: '#ddd',
                                '&:hover': { borderColor: '#bbb', bgcolor: 'rgba(0,0,0,0.04)' },
                              }}
                            >
                              Edit
                            </Button>
                          ) : null}
                          {showNewProductActions ? (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                disabled={submitting}
                                startIcon={<CheckCircleIcon />}
                                onClick={() => {
                                  setApproveNewProductError(null)
                                  setApproveNewProductId(item.inventory_sync_new_product_id)
                                }}
                                sx={{ ...approveButtonSx, ...lineItemActionButtonSx }}
                              >
                                Approve product
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={submitting}
                                startIcon={<CancelIcon />}
                                onClick={() => setRejectNewProductId(item.inventory_sync_new_product_id)}
                                sx={lineItemActionButtonSx}
                              >
                                Reject
                              </Button>
                            </>
                          ) : null}
                          {lineReviewStatus === 'pending' &&
                          !showNewProductActions &&
                          (item.line_type !== 'new' ||
                            item.new_product_review_status === 'approved') ? (
                            <>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                disabled={submitting}
                                startIcon={<CheckCircleIcon />}
                                onClick={() => handleApproveLineItem(item.id)}
                                sx={{ ...approveButtonSx, ...lineItemActionButtonSx }}
                              >
                                Approve line
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                disabled={submitting}
                                startIcon={<CancelIcon />}
                                onClick={() => handleRejectLineItem(item.id)}
                                sx={lineItemActionButtonSx}
                              >
                                Reject line
                              </Button>
                            </>
                          ) : null}
                        </Stack>
                      </TableCell>
                    ) : null}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        )}
        {allItems.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center', color: '#999' }}>
            <Typography>No items in this sync.</Typography>
          </Box>
        )}
        {allItems.length > 0 && filteredItems.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center', color: '#999' }}>
            <Typography>
              No{' '}
              {lineReviewStatusFilter === 'all'
                ? ''
                : `${LINE_REVIEW_STATUS_FILTERS[lineReviewStatusFilter]?.label.toLowerCase()} `}
              {lineItemSection === 'all'
                ? 'line items'
                : LINE_ITEM_SECTIONS[lineItemSection]?.label.toLowerCase()}{' '}
              {lineItemSearchQuery.trim()
                ? `match "${lineItemSearchQuery.trim()}"`
                : 'match the current filters'}
              .
            </Typography>
          </Box>
        )}
        {lineItemsPagination}
      </Card>
      )}

      <Dialog
        open={approveAllDialogOpen}
        onClose={() => !submitting && !approveAllRunning && setApproveAllDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Approve all pending line items</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Approve all {pendingLineItemsForApprovalCount} pending line item(s) in this
            sync in batches of {APPROVE_ALL_BATCH_SIZE}? Already approved or rejected lines
            will be left unchanged. Lines tied to rejected new products cannot be approved
            and will remain pending.
          </DialogContentText>
          {allItems.some(
            (item) =>
              isPendingLineItemForApproval(item) &&
              item.new_product_review_status === 'rejected',
          ) ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              Some pending lines belong to rejected new products and will be skipped.
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setApproveAllDialogOpen(false)}
            disabled={submitting || approveAllRunning}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApproveAllLineItems}
            variant="contained"
            color="success"
            startIcon={<DoneAllIcon />}
            disabled={submitting || approveAllRunning}
            sx={approveButtonSx}
          >
            Approve all pending
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={approveAllProgress != null}
        onClose={() => {
          if (!approveAllRunning) closeApproveAllProgress()
        }}
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown={approveAllRunning}
      >
        <DialogTitle>
          {approveAllProgress?.status === 'running'
            ? 'Approving line items'
            : approveAllProgress?.status === 'success'
              ? 'All line items approved'
              : 'Approval complete'}
        </DialogTitle>
        <DialogContent>
          {approveAllProgress ? (
            <Box sx={{ pt: 0.5 }}>
              <LinearProgress
                variant="determinate"
                value={
                  approveAllProgress.total > 0
                    ? Math.round(
                        (approveAllProgress.processed / approveAllProgress.total) * 100,
                      )
                    : 0
                }
                color={
                  approveAllProgress.status === 'partial'
                    ? 'warning'
                    : approveAllProgress.status === 'success'
                      ? 'success'
                      : 'primary'
                }
                sx={{ height: 8, borderRadius: 1, mb: 2 }}
              />
              {approveAllProgress.status === 'running' ? (
                <Typography variant="body2" color="text.secondary">
                  Batch {approveAllProgress.batch} of {approveAllProgress.batchTotal} ·{' '}
                  {approveAllProgress.processed} / {approveAllProgress.total} line items
                </Typography>
              ) : approveAllProgress.status === 'success' ? (
                <Typography variant="body2" color="text.secondary">
                  Approved {approveAllProgress.approved} line item(s).
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Approved {approveAllProgress.approved} of {approveAllProgress.total}{' '}
                    line item(s). {approveAllProgress.failed} failed — see highlighted rows
                    in the table.
                  </Typography>
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Failed line items are highlighted in the table below.
                  </Alert>
                </>
              )}
            </Box>
          ) : null}
        </DialogContent>
        {!approveAllRunning && approveAllProgress?.status !== 'success' ? (
          <DialogActions>
            <Button onClick={closeApproveAllProgress} variant="contained">
              Close
            </Button>
          </DialogActions>
        ) : null}
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Delete Inventory Sync</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will <strong>permanently delete</strong> Sync #{syncId}
            {sync.status !== 'rejected' ? ' and reject all its pending line items' : ''}
            . Approved and reverted syncs cannot be deleted.
          </DialogContentText>
          <TextField
            label="Reason (optional)"
            fullWidth
            multiline
            minRows={2}
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            startIcon={<DeleteIcon />}
          >
            {deleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={revertDialogOpen}
        onClose={() => !reverting && !revertPreflightLoading && setRevertDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Revert Inventory Sync</DialogTitle>
        <DialogContent>
          {revertPreflightLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <>
              <DialogContentText sx={{ mb: 2 }}>
                This will remove all stock added by this sync from the shop.
              </DialogContentText>
              <DialogContentText sx={{ mb: 2 }}>
                Items already sold to customers will stay sold. Those sales will no
                longer point to a stock lot.
              </DialogContentText>
              <DialogContentText sx={{ mb: 2 }}>
                This cannot be undone from the app. Products added to the catalog
                during this sync will stay in the product list.
              </DialogContentText>
              {revertPreflight ? (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    About to remove{' '}
                    <strong>{formatCount(revertPreflight.lots_count)}</strong> stock
                    lot(s) (
                    <strong>
                      {formatCount(revertPreflight.units_on_hand_to_remove)}
                    </strong>{' '}
                    units still in stock).
                  </Typography>
                  {Number(revertPreflight.units_already_sold) > 0 ? (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>
                        {formatCount(revertPreflight.units_already_sold)}
                      </strong>{' '}
                      units from this sync were already sold and will stay sold.
                    </Typography>
                  ) : null}
                  {Number(revertPreflight.new_catalog_products_count) > 0 ? (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>
                        {formatCount(revertPreflight.new_catalog_products_count)}
                      </strong>{' '}
                      catalog product(s) created during this sync will be left in the
                      product list.
                    </Typography>
                  ) : null}
                </Alert>
              ) : null}
              <TextField
                label="Reason (optional)"
                fullWidth
                multiline
                minRows={2}
                value={revertReason}
                onChange={(e) => setRevertReason(e.target.value)}
                disabled={reverting}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRevertDialogOpen(false)}
            disabled={reverting || revertPreflightLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleRevert}
            variant="contained"
            color="warning"
            disabled={reverting || revertPreflightLoading || !revertPreflight}
            startIcon={
              reverting ? <CircularProgress size={16} /> : <UndoIcon />
            }
          >
            {reverting ? 'Reverting…' : 'Confirm Revert'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={approveDialogOpen} onClose={() => !submitting && setApproveDialogOpen(false)}>
        <DialogTitle>Approve Inventory Sync</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Approving this sync will add the listed quantities to the shop&apos;s
            inventory as new stock lots (one lot per approved line). Admin
            inventory will not be modified.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleApprove}
            variant="contained"
            color="success"
            startIcon={submitting ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            disabled={submitting || allLineItemsRejected}
            sx={approveButtonSx}
          >
            {submitting ? 'Approving...' : 'Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkRejectDialogOpen}
        onClose={() => !submitting && setBulkRejectDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject selected line items</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Reject {selectedCount} selected line item(s). They will not be imported when
            the sync is approved.
          </DialogContentText>
          <TextField
            label="Reason (optional)"
            fullWidth
            multiline
            minRows={2}
            value={bulkRejectReason}
            onChange={(e) => setBulkRejectReason(e.target.value)}
            disabled={submitting}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkRejectDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkRejectSelected}
            variant="contained"
            color="error"
            disabled={submitting || selectedEligibleLineItemIds.length === 0}
          >
            Reject selected
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rejectDialogOpen} onClose={() => !submitting && setRejectDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Reject Inventory Sync</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Rejecting will mark this sync as <strong>rejected</strong>. No
            inventory will be added.
          </DialogContentText>
          <TextField
            label="Reason (optional)"
            fullWidth
            multiline
            minRows={2}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={submitting}
          >
            {submitting ? 'Rejecting...' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={modifyDialogOpen}
        onClose={() => !uploadingCsv && setModifyDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Modify Line Items</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Updates are matched using <strong>Line Item No.</strong>. The preview includes
            line item number, article code, product name, quantity, Selling Price, MRP, and expiry.
            Changes to article code and product name are ignored. Only quantity, Selling Price, MRP,
            and expiry are editable.
          </DialogContentText>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <Button variant="outlined" onClick={handleDownloadSampleCsv} startIcon={<DownloadIcon />}>
              Download sample CSV
            </Button>
            <Button
              variant="outlined"
              onClick={() => csvInputRef.current?.click()}
              startIcon={<UploadFileIcon />}
              disabled={uploadingCsv}
            >
              Choose CSV
            </Button>
            <Button
              variant="contained"
              onClick={handleUploadCsv}
              startIcon={<UploadFileIcon />}
              disabled={uploadingCsv || !selectedCsvFile}
            >
              {uploadingCsv ? 'Uploading...' : 'Upload'}
            </Button>
            <input
              ref={csvInputRef}
              hidden
              type="file"
              accept=".csv,text/csv"
              onChange={handleCsvFileSelect}
            />
          </Stack>
          {selectedCsvFile && (
            <Box
              sx={{
                mb: 2,
                px: 1.5,
                py: 1,
                border: '1px solid #eee',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Typography variant="body2" sx={{ color: '#666' }}>
                Selected file: {selectedCsvFile.name}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setSelectedCsvFile(null)}
                disabled={uploadingCsv}
                aria-label="Remove selected file"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          {csvResult && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Processed: {csvResult.processed || 0}, Updated: {csvResult.updated || 0}, Skipped:{' '}
              {csvResult.skipped || 0}
              {Array.isArray(csvResult.errors) && csvResult.errors.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  {csvResult.errors.slice(0, 5).map((err) => (
                    <Typography key={`${err.row_number}-${err.error_message}`} variant="caption" display="block">
                      Row {err.row_number}: {err.error_message}
                    </Typography>
                  ))}
                </Box>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModifyDialogOpen(false)} disabled={uploadingCsv}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkLinkDialogOpen}
        onClose={() => {
          if (bulkLinkUploading || bulkLinkConfirming) return
          setBulkLinkDialogOpen(false)
          resetBulkLinkDialogState()
        }}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Bulk Link to Existing Products</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Download pending new-product line items, fill in <strong>External Article Code</strong>{' '}
            for each row, then upload and validate. Review the preview before confirming the bulk
            link. Required columns: Line Item No., Article Code, Product Name, Quantity, Selling Price,
            MRP, Expiry Date, and External Article Code.
          </DialogContentText>
          {bulkLinkEligibleItems.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              No pending new-product lines are available to bulk link.
            </Alert>
          ) : null}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              onClick={handleDownloadBulkLinkLineItemsCsv}
              startIcon={<DownloadIcon />}
              disabled={bulkLinkEligibleItems.length === 0}
            >
              Download line items
            </Button>
            <Button
              variant="outlined"
              onClick={() => bulkLinkCsvInputRef.current?.click()}
              startIcon={<UploadFileIcon />}
              disabled={bulkLinkUploading || bulkLinkConfirming}
            >
              Choose CSV
            </Button>
            <Button
              variant="contained"
              onClick={handleValidateBulkLinkCsv}
              startIcon={<UploadFileIcon />}
              disabled={bulkLinkUploading || bulkLinkConfirming || !selectedBulkLinkCsvFile}
            >
              {bulkLinkUploading ? 'Validating...' : 'Validate upload'}
            </Button>
            <input
              ref={bulkLinkCsvInputRef}
              hidden
              type="file"
              accept=".csv,text/csv"
              onChange={handleBulkLinkCsvFileSelect}
            />
          </Stack>
          {selectedBulkLinkCsvFile && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 2,
                p: 1,
                bgcolor: '#f5f5f5',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" sx={{ flexGrow: 1 }}>
                Selected file: {selectedBulkLinkCsvFile.name}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  setSelectedBulkLinkCsvFile(null)
                  setBulkLinkPreview(null)
                }}
                disabled={bulkLinkUploading || bulkLinkConfirming}
                aria-label="Remove selected file"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
          {bulkLinkPreview && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Processed {bulkLinkPreview.processed || 0} row(s):{' '}
                {bulkLinkPreview.ready?.length || 0} ready to link,{' '}
                {bulkLinkPreview.failed?.length || 0} failed.
              </Alert>
              {(bulkLinkPreview.ready?.length || 0) > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Ready to link ({bulkLinkPreview.ready.length})
                  </Typography>
                  <TableContainer sx={{ maxHeight: 260, border: '1px solid #eee', borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Row</TableCell>
                          <TableCell>Line Item No.</TableCell>
                          <TableCell>Product Name</TableCell>
                          <TableCell>External Article Code</TableCell>
                          <TableCell>Catalog Product</TableCell>
                          <TableCell align="right">Qty</TableCell>
                          <TableCell align="right">Cost</TableCell>
                          <TableCell align="right">MRP</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bulkLinkPreview.ready.map((row) => (
                          <TableRow key={`ready-${row.row_number}-${row.line_item_id}`}>
                            <TableCell>{row.row_number}</TableCell>
                            <TableCell>{row.line_item_id}</TableCell>
                            <TableCell>{row.product_name || '-'}</TableCell>
                            <TableCell>{row.external_article_code || '-'}</TableCell>
                            <TableCell>{row.catalog_product_name || '-'}</TableCell>
                            <TableCell align="right">{row.quantity}</TableCell>
                            <TableCell align="right">{Number(row.cost_price || 0).toFixed(2)}</TableCell>
                            <TableCell align="right">{Number(row.mrp || 0).toFixed(2)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              {(bulkLinkPreview.failed?.length || 0) > 0 && (
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#b91c1c' }}>
                    Failed ({bulkLinkPreview.failed.length})
                  </Typography>
                  <TableContainer sx={{ maxHeight: 260, border: '1px solid #fecaca', borderRadius: 1 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Row</TableCell>
                          <TableCell>Line Item No.</TableCell>
                          <TableCell>Product Name</TableCell>
                          <TableCell>External Article Code</TableCell>
                          <TableCell>Reason</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {bulkLinkPreview.failed.map((row) => (
                          <TableRow key={`failed-${row.row_number}-${row.line_item_id || 'na'}`}>
                            <TableCell>{row.row_number}</TableCell>
                            <TableCell>{row.line_item_id ?? '-'}</TableCell>
                            <TableCell>{row.product_name || '-'}</TableCell>
                            <TableCell>{row.external_article_code || '-'}</TableCell>
                            <TableCell sx={{ color: '#b91c1c' }}>{row.error_message}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
          {bulkLinkConfirmResult && (
            <Alert
              severity={
                Array.isArray(bulkLinkConfirmResult.errors) &&
                bulkLinkConfirmResult.errors.length > 0
                  ? 'warning'
                  : 'success'
              }
              sx={{ mb: 2 }}
            >
              Confirmed: Updated {bulkLinkConfirmResult.updated || 0}, Linked:{' '}
              {bulkLinkConfirmResult.linked || 0}
              {Array.isArray(bulkLinkConfirmResult.errors) &&
                bulkLinkConfirmResult.errors.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    {bulkLinkConfirmResult.errors.map((err) => (
                      <Typography
                        key={`confirm-err-${err.line_item_id}-${err.error_message}`}
                        variant="caption"
                        display="block"
                      >
                        Line Item No. {err.line_item_id}: {err.error_message}
                      </Typography>
                    ))}
                  </Box>
                )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBulkLinkDialogOpen(false)
              resetBulkLinkDialogState()
            }}
            disabled={bulkLinkUploading || bulkLinkConfirming}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmBulkLink}
            disabled={
              bulkLinkUploading ||
              bulkLinkConfirming ||
              !bulkLinkPreview?.ready?.length
            }
          >
            {bulkLinkConfirming ? 'Linking...' : 'Confirm bulk link'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(approveNewProductId)}
        onClose={() => {
          if (submitting) return
          setApproveNewProductId(null)
          setApproveNewProductError(null)
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Approve new product</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {approveNewProductPreview?.product_name ? (
              <>
                You are approving <strong>{approveNewProductPreview.product_name}</strong> for the
                {approveNewProductPreview?.source === 'external'
                  ? ' external product catalog.'
                  : ' Shwapno catalog.'}
              </>
            ) : (
              <>You are approving this product for the catalog.</>
            )}
          </DialogContentText>
          {approveNewProductPreview ? (
            <Box sx={{ mb: 2 }}>
              <LineItemBarcodes item={approveNewProductPreview} />
            </Box>
          ) : null}
          {approveNewProductError ? (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                isFixableNewProductApproveError({ message: approveNewProductError }) ? (
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => {
                      if (approveNewProductId) {
                        openEditForNewProduct(approveNewProductId)
                      }
                      setApproveNewProductId(null)
                      setApproveNewProductError(null)
                    }}
                  >
                    Edit product
                  </Button>
                ) : undefined
              }
            >
              {approveNewProductError}
            </Alert>
          ) : null}
          <Alert severity="warning" sx={{ mb: 0 }}>
            After approval, product details (name, article code, barcodes, and image) for this new
            product request can no longer be edited from this sync. Quantity, cost, MRP, and expiry
            on line items can still be adjusted until the sync is approved or rejected.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApproveNewProductId(null)
              setApproveNewProductError(null)
            }}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={() => approveNewProductId && handleApproveNewProduct(approveNewProductId)}
            variant="contained"
            color="success"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} /> : <CheckCircleIcon />}
            sx={approveButtonSx}
          >
            {submitting ? 'Approving...' : 'Approve product'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(skuConflictPrompt)}
        onClose={() => {
          if (skuConflictSubmitting) return
          setSkuConflictPrompt(null)
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>SKU already in catalog</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            A product already exists with article code{' '}
            <strong>{skuConflictPrompt?.sku || '—'}</strong>
            {skuConflictPrompt?.existingProduct?.product_name ? (
              <>
                : <strong>{skuConflictPrompt.existingProduct.product_name}</strong>
              </>
            ) : null}
            . Choose how to continue.
          </DialogContentText>
          {skuConflictPrompt?.message ? (
            <Alert severity="info" sx={{ mb: 0 }}>
              {skuConflictPrompt.message}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
          <Button
            onClick={() => setSkuConflictPrompt(null)}
            disabled={skuConflictSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="outlined"
            onClick={handleSkuConflictLinkDifferent}
            disabled={skuConflictSubmitting}
            startIcon={<LinkIcon />}
          >
            Link to a different product
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleConfirmLinkMatchingSku}
            disabled={
              skuConflictSubmitting ||
              !isExistingProductLinkable(skuConflictPrompt?.existingProduct)
            }
            startIcon={
              skuConflictSubmitting ? <CircularProgress size={16} /> : <CheckCircleIcon />
            }
            sx={approveButtonSx}
          >
            {skuConflictSubmitting
              ? 'Linking...'
              : 'Link & approve matching SKU'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(rejectNewProductId)}
        onClose={() => !submitting && setRejectNewProductId(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Reject new product</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This product will not be added to the catalog or partner inventory when the sync is
            approved.
          </DialogContentText>
          <TextField
            label="Reason (optional)"
            fullWidth
            multiline
            minRows={2}
            value={rejectNewProductReason}
            onChange={(e) => setRejectNewProductReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectNewProductId(null)} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleRejectNewProductConfirm} color="error" variant="contained" disabled={submitting}>
            Reject product
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={rowEditOpen}
        onClose={() => {
          if (rowEditSaving || createInternalSubmitting) return
          setRowEditOpen(false)
          setRowEditError(null)
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Edit Line Item</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {rowEditError ? (
              <Alert severity="error" onClose={() => setRowEditError(null)}>
                {rowEditError}
              </Alert>
            ) : null}
            <TextField
              label="Line Item No."
              value={editingItem?.id || ''}
              disabled
            />
            <TextField
              label="Product Name"
              value={rowForm.product_name}
              onChange={(e) => setRowForm((prev) => ({ ...prev, product_name: e.target.value }))}
              disabled={editingItem?.line_type !== 'new'}
            />
            <TextField
              label="Identifier"
              value={rowForm.article_code}
              onChange={(e) =>
                setRowForm((prev) => ({ ...prev, article_code: e.target.value }))
              }
              disabled={editingItem?.line_type !== 'new'}
              helperText={
                editingItem?.new_product_source === 'external'
                  ? 'Optional. Leave blank to auto-assign (9xxxxxx) on approval.'
                  : undefined
              }
            />
            {editingItem?.line_type === 'new' ? (
              <>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Barcodes
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Add barcode"
                      value={barcodeInput}
                      onChange={(e) => setBarcodeInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          addBarcodeToForm()
                        }
                      }}
                      placeholder="Type barcode and press Enter"
                    />
                    <IconButton
                      onClick={addBarcodeToForm}
                      sx={{ border: '1px solid #e0e0e0', borderRadius: 1, alignSelf: 'center' }}
                      aria-label="Add barcode"
                    >
                      <AddIcon />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                    {rowForm.barcodes.length === 0 ? (
                      <Typography variant="body2" sx={{ color: '#888' }}>
                        No barcodes added.
                      </Typography>
                    ) : (
                      rowForm.barcodes.map((code) => (
                        <Chip
                          key={code}
                          label={code}
                          size="small"
                          onDelete={() => removeBarcodeFromForm(code)}
                          sx={{ fontFamily: 'monospace' }}
                        />
                      ))
                    )}
                  </Box>
                </Box>
                <TextField
                  label="Picture URL"
                  value={rowForm.image_url}
                  onChange={(e) =>
                    setRowForm((prev) => ({ ...prev, image_url: e.target.value }))
                  }
                />
              </>
            ) : null}
            <TextField
              label="Quantity"
              type="number"
              inputProps={{ min: 1, step: 1 }}
              value={rowForm.quantity}
              onChange={(e) => setRowForm((prev) => ({ ...prev, quantity: e.target.value }))}
            />
            <TextField
              label="Selling Price"
              type="number"
              inputProps={{ min: 0.01, step: '0.01' }}
              value={rowForm.unit_cost}
              onChange={(e) => setRowForm((prev) => ({ ...prev, unit_cost: e.target.value }))}
            />
            <TextField
              label="MRP"
              type="number"
              inputProps={{ min: 0.01, step: '0.01' }}
              value={rowForm.unit_price}
              onChange={(e) => setRowForm((prev) => ({ ...prev, unit_price: e.target.value }))}
            />
            <TextField
              label="Expiry"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={rowForm.expiry_date}
              onChange={(e) => setRowForm((prev) => ({ ...prev, expiry_date: e.target.value }))}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={!!rowForm.is_non_shwapno}
                  onChange={(e) =>
                    setRowForm((prev) => ({
                      ...prev,
                      is_non_shwapno: e.target.checked,
                    }))
                  }
                />
              }
              label="Non-Shwapno inventory lot"
            />
            {isNewProductPending(editingItem) && sync?.status === 'submitted' ? (
              <>
                <Divider sx={{ my: 1 }} />
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Catalog mapping
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                    Link this synced product to an existing Shwapno catalog item, or enroll it
                    as a new internal product before approving line items.
                  </Typography>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                    <Button
                      variant="outlined"
                      startIcon={<LinkIcon />}
                      onClick={handleOpenLinkDialog}
                      disabled={rowEditSaving || createInternalSubmitting}
                      sx={{ flex: 1 }}
                    >
                      Link to Existing Product
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<AddBusinessIcon />}
                      onClick={handleCreateAsNewInternalProduct}
                      disabled={rowEditSaving || createInternalSubmitting}
                      sx={{ flex: 1 }}
                    >
                      {createInternalSubmitting ? 'Creating...' : 'Create New Product'}
                    </Button>
                  </Stack>
                </Box>
              </>
            ) : null}
            {editingItem?.line_type === 'new' &&
            editingItem?.new_product_review_status === 'approved' &&
            editingItem?.admin_product_id ? (
              <Alert severity="info">
                Mapped to internal product ID: {editingItem.admin_product_id}
                {editingItem.article_code ? ` (${editingItem.article_code})` : ''}
              </Alert>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRowEditOpen(false)
              setRowEditError(null)
            }}
            disabled={rowEditSaving || createInternalSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveRow}
            variant="contained"
            disabled={rowEditSaving || createInternalSubmitting}
          >
            {rowEditSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={linkDialogOpen}
        onClose={() => {
          if (linkSubmitting) return
          setLinkDialogOpen(false)
          setLinkDialogError(null)
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Link to Existing Product</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Search Shwapno&apos;s internal catalog by product name or article code, then select
            the product to map this sync request to.
          </DialogContentText>
          {linkDialogError ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLinkDialogError(null)}>
              {linkDialogError}
            </Alert>
          ) : null}
          <Autocomplete
            fullWidth
            loading={linkProductLoading}
            options={linkProductOptions}
            value={selectedLinkProduct}
            onChange={(_, value) => setSelectedLinkProduct(value)}
            inputValue={linkProductSearch}
            onInputChange={(_, value, reason) => {
              if (reason === 'input' || reason === 'clear') {
                setLinkProductSearch(value)
              }
            }}
            getOptionLabel={catalogProductLabel}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            filterOptions={(options) => options}
            renderOption={(props, option) => {
              const { key, ...rest } = props
              const articleCode = option.sku || option.article_code || '—'
              const isNonShwapno = isCatalogProductNonShwapno(option)
              const mrpLabel = formatCatalogMrp(option)
              return (
                <li key={key} {...rest}>
                  <Box sx={{ py: 0.5, width: '100%', minWidth: 0 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ flexWrap: 'wrap', gap: 0.5 }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          lineHeight: 1.35,
                          wordBreak: 'break-word',
                        }}
                      >
                        {option.product_name || option.name || 'Unnamed product'}
                      </Typography>
                      <Chip
                        size="small"
                        label={isNonShwapno ? 'Non-Shwapno' : 'Shwapno'}
                        color={isNonShwapno ? 'warning' : 'default'}
                        variant={isNonShwapno ? 'filled' : 'outlined'}
                        sx={{ height: 22, fontSize: '0.7rem' }}
                      />
                    </Stack>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.25 }}>
                      Article code: {articleCode}
                      {' · '}
                      MRP: {mrpLabel || '—'}
                    </Typography>
                  </Box>
                </li>
              )
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search catalog products"
                placeholder="Product name or article code"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {linkProductLoading ? (
                        <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkDialogOpen(false)} disabled={linkSubmitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleLinkToExistingProduct}
            disabled={!selectedLinkProduct || linkSubmitting}
          >
            {linkSubmitting ? 'Linking...' : 'Link Product'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  )
}

export default InventorySyncDetailPage
